import { useState, useEffect, useRef, useCallback, useMemo } from "react";

// ─── CONFIG ────────────────────────────────────────────────────────
const API_BASE = window.VAULT_API_URL || "https://vault-api-g8rb.onrender.com";

// ─── THEME ─────────────────────────────────────────────────────────
const themes = {
  dark: {
    bg: "#000000", card: "#0a0a0a", border: "#1a1a1a", text: "#f5ecd4",
    textSec: "#e5e7eb", textMuted: "#9ca3af", textDim: "#6b7280",
    accent: "#d4b65a", accentGrad: "linear-gradient(135deg, #d4b65a, #f0dc90)",
    accentBg: "#d4b65a22", green: "#5eead4", greenBg: "#065f4620",
    red: "#fca5a5", redBg: "#7f1d1d30", pink: "#f9a8d4", blue: "#93c5fd",
    yellow: "#fcd34d", headerBorder: "#0a0a0a", inputBg: "#0a0a0a",
    modalBg: "#050505", navBg: "#000000",
  },
  light: {
    bg: "#f5f5f0", card: "#ffffff", border: "#e5e5e0", text: "#1a1a1a",
    textSec: "#333333", textMuted: "#666666", textDim: "#999999",
    accent: "#8b6914", accentGrad: "linear-gradient(135deg, #8b6914, #c9a84c)",
    accentBg: "#8b691418", green: "#16a34a", greenBg: "#16a34a20",
    red: "#dc2626", redBg: "#dc262620", pink: "#db2777", blue: "#2563eb",
    yellow: "#d97706", headerBorder: "#e5e5e0", inputBg: "#f0f0eb",
    modalBg: "#ffffff", navBg: "#f5f5f0",
  },
};

// ─── API LAYER ─────────────────────────────────────────────────────
const api = {
  async _fetch(url, opts = {}, retries = 2) {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const r = await fetch(url, opts);
        if (r.ok) return r.json();
        // 5xx = server issue, retry. 4xx = our bug, fail immediately.
        if (r.status >= 500 && attempt < retries) {
          await new Promise(res => setTimeout(res, 800 * (attempt + 1)));
          continue;
        }
        let body = "";
        try { body = (await r.text()).slice(0, 150); } catch {}
        throw new Error(`${r.status}${body ? ": " + body : ""}`);
      } catch (e) {
        // Network error — retry once
        if (attempt < retries && e.name === "TypeError") {
          await new Promise(res => setTimeout(res, 800 * (attempt + 1)));
          continue;
        }
        throw e;
      }
    }
  },
  async get(p) { return this._fetch(`${API_BASE}${p}`); },
  async post(p, d) { return this._fetch(`${API_BASE}${p}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d) }); },
  async del(p) { return this._fetch(`${API_BASE}${p}`, { method: "DELETE" }); },
};

// ─── TX DETAIL CACHE (module-scoped, survives remounts) ────────────
// Keyed by `${accountName}|${isCC}` → { this, last, fetchedAt }
const txDetailCache = new Map();
const TX_CACHE_TTL = 60 * 1000; // 1 minute — stale after that

// ─── TX QUEUE (instant save) ───────────────────────────────────────
const txQueue = {
  pending: [], processing: false,
  add(tx) { this.pending.push({ ...tx, _qId: Date.now() }); this.process(); },
  async process() {
    if (this.processing) return; this.processing = true;
    while (this.pending.length > 0) {
      const tx = this.pending[0];
      try {
        await api.post("/api/transactions", { accountName: tx.accountName, accountId: tx.accountId, note: tx.note, amount: tx.isExpense ? -Math.abs(tx.amount) : Math.abs(tx.amount), labels: tx.labels, category: tx.category, subcategory: tx.subcategory, date: tx.date, accountType: tx.accountType });
        this.pending.shift();
      } catch { await new Promise(r => setTimeout(r, 5000)); }
    }
    this.processing = false;
  },
};

// ─── FORMATTING (no rounding, exact decimals) ──────────────────────
const fmtFull = (n) => {
  if (window.__vaultHide) return "₹ •••••";
  const abs = Math.abs(n || 0);
  const hasDecimals = abs % 1 !== 0;
  return "₹" + abs.toLocaleString("en-IN", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

// Short format for card headers (₹45.6K) but still no rounding of raw values
const fmtShort = (n) => {
  if (window.__vaultHide) return "₹ •••";
  const abs = Math.abs(n || 0);
  if (abs >= 100000) return "₹" + (abs / 100000).toFixed(1) + "L";
  if (abs >= 10000) return "₹" + (abs / 1000).toFixed(1) + "K";
  return fmtFull(n);
};

// Signed format — shows +/- prefix
const fmtSigned = (n) => {
  if (window.__vaultHide) return "₹ •••";
  const v = n || 0;
  const sign = v > 0 ? "+" : v < 0 ? "-" : "";
  return sign + fmtFull(Math.abs(v));
};

const displayName = (acc) => acc?.name || acc?.Name || "";

// ─── ANIMATIONS ────────────────────────────────────────────────────
const FadeIn = ({ children, delay = 0 }) => {
  const [v, setV] = useState(false);
  useEffect(() => { const t = setTimeout(() => setV(true), delay); return () => clearTimeout(t); }, [delay]);
  return <div style={{ opacity: v ? 1 : 0, transform: v ? "translateY(0)" : "translateY(12px)", transition: "all 0.4s cubic-bezier(.22,.68,0,.71)" }}>{children}</div>;
};

// ─── TOAST ─────────────────────────────────────────────────────────
function Toast({ message, type = "success", onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2000); return () => clearTimeout(t); }, [onDone]);
  const bg = type === "success" ? "#065f46" : type === "error" ? "#7f1d1d" : "#1e293b";
  return <div style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", background: bg, color: "#f0e6d2", padding: "12px 24px", borderRadius: 12, fontSize: 14, fontWeight: 600, zIndex: 9999, boxShadow: "0 8px 32px rgba(0,0,0,0.5)", animation: "slideDown 0.3s ease" }}>{type === "success" ? "✓ " : type === "error" ? "✗ " : ""}{message}</div>;
}

// ─── ACCOUNT SELECTOR ──────────────────────────────────────────────
function AccountSelector({ accounts, recentAccounts, onSelect, onClose, T }) {
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  // Don't auto-focus search — keyboard would hide the RECENT list. User can tap search bar if they want to type.
  const q = search.toLowerCase();
  const filtered = accounts.filter(a => (a.name || "").toLowerCase().includes(q) || (a.nicknames || "").toLowerCase().includes(q) || (a.id || "").toLowerCase().includes(q));
  const cc = filtered.filter(a => a.type === "CreditCard");
  const sav = filtered.filter(a => ["SavingAccount", "Savings", "Cash", "Bonus", "General"].includes(a.type));
  // Recent accounts (only show when not searching)
  const recent = !q && recentAccounts ? recentAccounts.filter(name => accounts.some(a => a.name === name)).map(name => accounts.find(a => a.name === name)).filter(Boolean).slice(0, 5) : [];

  const Group = ({ label, color, items }) => items.length > 0 && <>
    <div style={{ padding: "10px 20px 6px", fontSize: 10, letterSpacing: 2, color, textTransform: "uppercase" }}>{label}</div>
    {items.map(a => <button key={a.id || a.name} onClick={() => onSelect(a)} style={{ width: "100%", padding: "14px 20px", background: "transparent", border: "none", borderBottom: `1px solid ${T.bg}`, color: T.textSec, fontSize: 15, textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between" }}>
      <div><div style={{ fontWeight: 500 }}>{a.name}</div>{a.nicknames && <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>{a.nicknames.split(",")[0].trim()}</div>}</div>
      {a.billDay ? <span style={{ fontSize: 11, color: T.textDim }}>Bill: {a.billDay}th</span> : null}
    </button>)}
  </>;

  return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose}>
    <div style={{ width: "100%", maxWidth: 420, maxHeight: "80vh", background: T.modalBg, borderRadius: "20px 20px 0 0", overflow: "hidden", animation: "slideUp 0.3s ease" }} onClick={e => e.stopPropagation()}>
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: T.textDim, margin: "0 auto 12px" }} />
        <input ref={ref} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search accounts..." style={{ width: "100%", padding: "12px 16px", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 12, color: T.text, fontSize: 15, outline: "none", boxSizing: "border-box" }} />
      </div>
      <div style={{ overflowY: "auto", maxHeight: "60vh" }}>
        {recent.length > 0 && <Group label="RECENT" color={T.accent} items={recent} />}
        <Group label="CREDIT CARDS" color={T.accent} items={cc} />
        <Group label="SAVINGS & CASH" color={T.green} items={sav} />
        {filtered.length === 0 && <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>No accounts found</div>}
      </div>
    </div>
  </div>;
}

// ─── LABEL SELECTOR (multi-select modal) ───────────────────────────
function LabelSelector({ labels, recentLabels, selected, onToggle, onClose, onDone, T }) {
  const [search, setSearch] = useState("");
  const q = search.toLowerCase();

  // All labels with name normalized to string
  const all = labels.map(l => ({ name: l.name || l }));

  // Recent labels (usage-sorted, only when not searching)
  const recent = !q && recentLabels
    ? recentLabels
        .map(name => all.find(l => l.name === name))
        .filter(Boolean)
        .slice(0, 10)
    : [];

  // "All labels" section — alphabetical, optionally filtered by search
  const filtered = all
    .filter(l => !q || l.name.toLowerCase().includes(q))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const LabelRow = ({ label }) => {
    const isSel = selected.includes(label.name);
    return <button onClick={() => onToggle(label.name)} style={{
      width: "100%", padding: "14px 20px",
      background: isSel ? T.accentBg : "transparent",
      border: "none", borderBottom: `1px solid ${T.bg}`,
      color: isSel ? T.accent : T.textSec,
      fontSize: 15, textAlign: "left", cursor: "pointer",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      fontWeight: isSel ? 600 : 400,
    }}>
      <span>{label.name}</span>
      {isSel && <span style={{ fontSize: 16, color: T.accent }}>✓</span>}
    </button>;
  };

  return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose}>
    <div style={{ width: "100%", maxWidth: 420, maxHeight: "80vh", background: T.modalBg, borderRadius: "20px 20px 0 0", overflow: "hidden", animation: "slideUp 0.3s ease", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: T.textDim, margin: "0 auto 12px" }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search labels..." style={{ width: "100%", padding: "12px 16px", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 12, color: T.text, fontSize: 15, outline: "none", boxSizing: "border-box" }} />
      </div>
      <div style={{ overflowY: "auto", flex: 1 }}>
        {recent.length > 0 && <>
          <div style={{ padding: "10px 20px 6px", fontSize: 10, letterSpacing: 2, color: T.accent, textTransform: "uppercase" }}>RECENT</div>
          {recent.map(l => <LabelRow key={"r-" + l.name} label={l} />)}
        </>}
        {filtered.length > 0 && <>
          <div style={{ padding: "10px 20px 6px", fontSize: 10, letterSpacing: 2, color: T.textMuted, textTransform: "uppercase" }}>ALL LABELS</div>
          {filtered.map(l => <LabelRow key={l.name} label={l} />)}
        </>}
        {filtered.length === 0 && <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>No labels found</div>}
      </div>
      {/* Footer with Done button showing count */}
      <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.border}`, flexShrink: 0, background: T.modalBg }}>
        <button onClick={onDone} style={{
          width: "100%", padding: "12px", borderRadius: 12, border: "none",
          background: T.accentGrad, color: "#0a0f1e",
          fontSize: 14, fontWeight: 700, letterSpacing: 1, cursor: "pointer",
        }}>
          DONE{selected.length > 0 ? ` (${selected.length})` : ""}
        </button>
      </div>
    </div>
  </div>;
}

// ─── CATEGORY SELECTOR ─────────────────────────────────────────────
function CategorySelector({ categories, onSelect, onClose, T }) {
  const [exp, setExp] = useState(null);
  return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose}>
    <div style={{ width: "100%", maxWidth: 420, maxHeight: "80vh", background: T.modalBg, borderRadius: "20px 20px 0 0", overflow: "hidden", animation: "slideUp 0.3s ease" }} onClick={e => e.stopPropagation()}>
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: T.textDim, margin: "0 auto 12px" }} />
        <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>Select Category</div>
      </div>
      <div style={{ overflowY: "auto", maxHeight: "65vh" }}>
        {Object.entries(categories).map(([cat, subs]) => <div key={cat}>
          <button onClick={() => setExp(exp === cat ? null : cat)} style={{ width: "100%", padding: "14px 20px", background: exp === cat ? T.accentBg : "transparent", border: "none", borderBottom: `1px solid ${T.bg}`, color: exp === cat ? T.accent : T.textSec, fontSize: 15, textAlign: "left", cursor: "pointer", fontWeight: exp === cat ? 600 : 400, display: "flex", justifyContent: "space-between" }}>
            <span>{cat}</span><span style={{ transform: exp === cat ? "rotate(90deg)" : "none", transition: "0.2s" }}>›</span>
          </button>
          {exp === cat && subs.map(s => <button key={s} onClick={() => onSelect(cat, s)} style={{ width: "100%", padding: "12px 20px 12px 40px", background: "transparent", border: "none", borderBottom: `1px solid ${T.bg}08`, color: T.textMuted, fontSize: 14, textAlign: "left", cursor: "pointer" }}>{s}</button>)}
        </div>)}
      </div>
    </div>
  </div>;
}

// ─── ADD TRANSACTION ───────────────────────────────────────────────
function AddScreen({ accounts, labels, categories, onToast, T, recentAccounts, recentLabels, dataLoading, onTxAdded }) {
  const [amount, setAmount] = useState(""); const [note, setNote] = useState("");
  const [account, setAccount] = useState(null); const [selectedLabels, setSelectedLabels] = useState([]);
  const [category, setCategory] = useState(""); const [subcategory, setSubcategory] = useState("");
  const [isExpense, setIsExpense] = useState(true); const [showAcc, setShowAcc] = useState(false);
  const [showLbl, setShowLbl] = useState(false);
  const [showCat, setShowCat] = useState(false); const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }; const [date, setDate] = useState(todayLocal);
  const [showDate, setShowDate] = useState(false); const amtRef = useRef(null);
  const toggleLabel = l => setSelectedLabels(p => p.includes(l) ? p.filter(x => x !== l) : [...p, l]);
  const handleSave = () => {
    if (!amount || !account) { onToast("Enter amount and select account", "error"); return; }
    txQueue.add({ accountName: account.name, accountId: account.id, note, amount: parseFloat(amount), isExpense, labels: selectedLabels, category, subcategory, date, accountType: account.type });
    onToast(`${fmtFull(parseFloat(amount))} → ${displayName(account)}`, "success");
    setAmount(""); setNote(""); setSelectedLabels([]); setCategory(""); setSubcategory(""); setDate(todayLocal());
    setTimeout(() => amtRef.current?.focus(), 100);
    if (onTxAdded) setTimeout(onTxAdded, 3000); // signal refresh after tx syncs
  };
  const ls = { fontSize: 11, color: T.textMuted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 };
  const fs = { width: "100%", padding: "14px 16px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, color: T.text, fontSize: 15, outline: "none", boxSizing: "border-box", textAlign: "left", cursor: "pointer" };

  return <div style={{ padding: "0 20px 100px", overflowY: "auto", height: "100%" }}>
    {dataLoading && <div style={{ background: T.accentBg, borderRadius: 10, padding: "8px 16px", marginTop: 12, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 14, height: 14, border: `2px solid ${T.border}`, borderTopColor: T.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <span style={{ fontSize: 12, color: T.accent }}>Loading accounts & labels...</span>
    </div>}
    <FadeIn delay={50}><div style={{ display: "flex", background: T.card, borderRadius: 12, padding: 4, marginBottom: 24, marginTop: dataLoading ? 8 : 12 }}>
      {["Expense", "Income"].map(t => {
        const active = t === "Expense" ? isExpense : !isExpense;
        const bg = t === "Expense" ? `linear-gradient(135deg, ${T.red}, #ff8a80)` : `linear-gradient(135deg, ${T.green}, #6ee7a8)`;
        return <button key={t} onClick={() => setIsExpense(t === "Expense")} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "none", cursor: "pointer", background: active ? bg : "transparent", color: active ? "#0a0f1e" : T.textMuted, fontWeight: 600, fontSize: 14, transition: "all 0.2s" }}>{t}</button>;
      })}
    </div></FadeIn>
    <FadeIn delay={100}><div style={{ textAlign: "center", marginBottom: 28 }}>
      <div style={ls}>Amount</div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 2 }}>
        <span style={{ fontSize: 32, color: isExpense ? T.red : T.green, fontWeight: 500 }}>₹</span>
        <input ref={amtRef} type="number" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" style={{ background: "transparent", border: "none", outline: "none", fontSize: 48, color: isExpense ? T.red : T.green, fontWeight: 700, width: 200, textAlign: "center", fontFamily: "'DM Sans', sans-serif" }} />
      </div>
      <div style={{ width: 120, height: 2, margin: "8px auto 0", background: `linear-gradient(90deg, transparent, ${isExpense ? T.red : T.green}, transparent)` }} />
    </div></FadeIn>
    <FadeIn delay={150}><div style={{ marginBottom: 16 }}>
      <div style={ls}>Account</div>
      {/* Quick-pick: top recent accounts as tappable chips */}
      {(() => {
        const recents = (recentAccounts || [])
          .map(name => accounts.find(a => a.name === name))
          .filter(Boolean)
          .slice(0, 4);
        if (recents.length === 0) return null;
        return <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 8, paddingBottom: 4, WebkitOverflowScrolling: "touch" }}>
          {recents.map(a => {
            const active = account?.name === a.name;
            const isCC = a.type === "CreditCard";
            const tint = isCC ? T.accent : T.green;
            return <button key={a.name} onClick={() => setAccount(a)} style={{
              padding: "7px 14px",
              borderRadius: 18,
              border: `1px solid ${active ? tint : T.border}`,
              background: active ? tint + "22" : T.card,
              color: active ? tint : T.textSec,
              fontSize: 12,
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontWeight: active ? 600 : 500,
              flexShrink: 0,
              transition: "all 0.15s",
            }}>{displayName(a)}</button>;
          })}
        </div>;
      })()}
      <button onClick={() => setShowAcc(true)} style={{ ...fs, color: account ? T.text : T.textDim, display: "flex", justifyContent: "space-between", alignItems: "center" }}><span>{account ? displayName(account) : "Select account..."}</span><span style={{ fontSize: 10, letterSpacing: 1, fontWeight: 600, color: account?.type === "CreditCard" ? T.accent : T.green }}>{account?.type === "CreditCard" ? "CREDIT" : account ? "SAVINGS" : ""}</span></button>
    </div></FadeIn>
    <FadeIn delay={200}><div style={{ marginBottom: 16 }}><div style={ls}>Note</div><input value={note} onChange={e => setNote(e.target.value)} placeholder="What was this for?" style={{ ...fs, cursor: "text" }} /></div></FadeIn>
    <FadeIn delay={250}><div style={{ marginBottom: 16 }}>
      <div style={ls}>Labels</div>
      {/* Quick-pick: top 10 most-used labels as tappable chips (plus any currently selected that aren't in top 10) */}
      {(() => {
        const allLabels = labels.map(l => l.name || l);
        // Top 10 by usage
        const topFromRecent = (recentLabels || [])
          .filter(name => allLabels.includes(name))
          .slice(0, 10);
        // Include any selected labels that aren't in the top 10 — so selection is always visible
        const extras = selectedLabels.filter(name => !topFromRecent.includes(name) && allLabels.includes(name));
        const chips = [...topFromRecent, ...extras];
        // Fallback: if no usage data, show first 10 alphabetically
        const displayChips = chips.length > 0 ? chips : [...allLabels]
          .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
          .slice(0, 10);
        return <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {displayChips.map(n => {
            const a = selectedLabels.includes(n);
            return <button key={n} onClick={() => toggleLabel(n)} style={{ padding: "8px 16px", borderRadius: 20, border: `1px solid ${a ? T.accent : T.border}`, background: a ? T.accentBg : T.card, color: a ? T.accent : T.textMuted, fontSize: 13, cursor: "pointer", fontWeight: a ? 600 : 400, transition: "all 0.2s" }}>{n}</button>;
          })}
          {allLabels.length > displayChips.length && <button onClick={() => setShowLbl(true)} style={{ padding: "8px 16px", borderRadius: 20, border: `1px dashed ${T.border}`, background: "transparent", color: T.textMuted, fontSize: 13, cursor: "pointer", fontWeight: 500 }}>+ More</button>}
        </div>;
      })()}
    </div></FadeIn>
    <FadeIn delay={300}><div style={{ marginBottom: 16 }}><div style={ls}>Category</div><button onClick={() => setShowCat(true)} style={{ ...fs, color: category ? T.text : T.textDim }}>{category ? `${category}${subcategory ? " → " + subcategory : ""}` : "Select category..."}</button></div></FadeIn>
    <FadeIn delay={320}><div style={{ marginBottom: 24 }}><button onClick={() => setShowDate(!showDate)} style={{ background: "transparent", border: "none", color: T.textDim, fontSize: 12, cursor: "pointer", letterSpacing: 1, textTransform: "uppercase" }}>{showDate ? "▼" : "▶"} Backdate</button>
      {showDate && <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...fs, marginTop: 8, colorScheme: T === themes.dark ? "dark" : "light" }} />}</div></FadeIn>
    <FadeIn delay={350}><button onClick={handleSave} style={{ width: "100%", padding: "16px 0", borderRadius: 14, border: "none", cursor: "pointer", background: amount && account ? T.accentGrad : T.border, color: amount && account ? "#0a0f1e" : T.textDim, fontSize: 16, fontWeight: 700, letterSpacing: 1, boxShadow: amount && account ? "0 4px 24px #c9a84c33" : "none", transition: "all 0.3s" }}>SAVE TRANSACTION</button></FadeIn>
    {showAcc && <AccountSelector accounts={accounts} recentAccounts={recentAccounts} onSelect={a => { setAccount(a); setShowAcc(false); }} onClose={() => setShowAcc(false)} T={T} />}
    {showLbl && <LabelSelector labels={labels} recentLabels={recentLabels} selected={selectedLabels} onToggle={toggleLabel} onClose={() => setShowLbl(false)} onDone={() => setShowLbl(false)} T={T} />}
    {showCat && <CategorySelector categories={categories} onSelect={(c, s) => { setCategory(c); setSubcategory(s); setShowCat(false); }} onClose={() => setShowCat(false)} T={T} />}
  </div>;
}

// ─── TRANSACTION DETAIL (per-card drill-down) ──────────────────────
// Parses dates like "10-Apr-2026" or "2026-04-10" → ms since epoch
function parseTxDate(s) {
  if (!s) return 0;
  const mo = { jan:0, feb:1, mar:2, apr:3, may:4, jun:5, jul:6, aug:7, sep:8, oct:9, nov:10, dec:11 };
  const p = String(s).split("-");
  if (p.length === 3) {
    const d = parseInt(p[0]), m = mo[p[1].toLowerCase().slice(0,3)], y = parseInt(p[2]);
    if (!isNaN(d) && m !== undefined && !isNaN(y)) return new Date(y, m, d).getTime();
  }
  const d2 = new Date(s);
  return isNaN(d2) ? 0 : d2.getTime();
}

function TxDetailRow({ tx, isCreditCard, isLast, T }) {
  const isRefund = tx.amount > 0;
  const amtColor = isRefund ? T.green : T.text;
  const labels = String(tx.labels || "").toLowerCase();
  let tag = null;
  if (labels.includes("mummy") || labels.includes("pappa")) tag = "M";
  else if (labels.includes("friends")) tag = "F&F";

  return <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 4px", borderBottom: isLast ? "none" : `1px solid ${T.border}`, fontSize: 12 }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: T.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {tx.note || tx.category || "—"}
        {tag && <span style={{ marginLeft: 6, fontSize: 9, padding: "1px 5px", borderRadius: 4, background: tag === "M" ? T.pink + "22" : T.blue + "22", color: tag === "M" ? T.pink : T.blue, fontWeight: 600 }}>{tag}</span>}
      </div>
      <div style={{ color: T.textDim, fontSize: 10, marginTop: 2 }}>{tx.date}{tx.category ? ` · ${tx.category}` : ""}</div>
    </div>
    <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
      <div style={{ color: amtColor, fontWeight: 600 }}>{isRefund ? "+" : ""}{fmtFull(tx.amount)}</div>
      {!isCreditCard && tx.balance !== null && tx.balance !== undefined && (
        <div style={{ color: T.textDim, fontSize: 10, marginTop: 2 }}>bal {fmtFull(tx.balance)}</div>
      )}
    </div>
  </div>;
}

function TxSection({ title, report, isCreditCard, endingBalance, T }) {
  const rows = useMemo(() => {
    if (!report) return [];
    // Merge and sort OLDEST first — needed for balance walk to be chronological
    const merged = [
      ...(report.own || []),
      ...(report.mummy || []),
      ...(report.ff || []),
      ...(report.refunds || []),
    ].slice().sort((a, b) => parseTxDate(a.date) - parseTxDate(b.date));
    let built;
    if (isCreditCard) {
      built = merged.map(t => ({ ...t, balance: null }));
    } else {
      // Walk forward from opening balance so each row shows balance AFTER that txn
      const netSum = merged.reduce((s, t) => s + (Number(t.amount) || 0), 0);
      let running = (endingBalance || 0) - netSum;
      built = merged.map(t => { running += Number(t.amount) || 0; return { ...t, balance: running }; });
    }
    // Display newest first
    return built.reverse();
  }, [report, isCreditCard, endingBalance]);

  const net = report?.splits?.net ?? 0;

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: T.textMuted, fontWeight: 600 }}>
      <span>{title}</span>
      <span style={{ color: T.textSec, fontSize: 11 }}>{rows.length} {rows.length === 1 ? "txn" : "txns"} · {fmtFull(net)}</span>
    </div>
    {rows.length === 0 ? (
      <div style={{ padding: "8px 0", textAlign: "center", color: T.textDim, fontSize: 11 }}>No transactions</div>
    ) : (
      <div style={{ background: T.bg, borderRadius: 8, padding: "4px 8px" }}>
        {rows.map((t, i) => <TxDetailRow key={i} tx={t} isCreditCard={isCreditCard} isLast={i === rows.length - 1} T={T} />)}
      </div>
    )}
  </div>;
}

function CardTransactionDetail({ accountName, isCreditCard, currentBalance, T }) {
  const cacheKey = `${accountName}|${isCreditCard ? "cc" : "sav"}`;
  const cached = txDetailCache.get(cacheKey);
  const cacheFresh = cached && (Date.now() - cached.fetchedAt) < TX_CACHE_TTL;

  const [data, setData] = useState(cacheFresh ? { this: cached.this, last: cached.last } : null);
  const [loading, setLoading] = useState(!cacheFresh);
  const [error, setError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    // If we have fresh cached data, skip fetch entirely
    const c = txDetailCache.get(cacheKey);
    if (c && (Date.now() - c.fetchedAt) < TX_CACHE_TTL) {
      setData({ this: c.this, last: c.last });
      setLoading(false);
      return;
    }
    let cancelled = false;
    const thisType = isCreditCard ? "cc-this" : "sav-this";
    const lastType = isCreditCard ? "cc-last" : "sav-last";
    const q = encodeURIComponent(accountName);
    setLoading(true); setError(null);
    (async () => {
      try {
        const thisR = await api.get(`/api/report/${thisType}?account=${q}`);
        if (cancelled) return;
        const lastR = await api.get(`/api/report/${lastType}?account=${q}`);
        if (cancelled) return;
        txDetailCache.set(cacheKey, { this: thisR, last: lastR, fetchedAt: Date.now() });
        setData({ this: thisR, last: lastR });
        setLoading(false);
      } catch (e) {
        if (!cancelled) { setError(e.message || "Failed"); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [accountName, isCreditCard, cacheKey, retryTick]);

  if (loading) return <div style={{ padding: "12px 0", textAlign: "center", color: T.textDim, fontSize: 12 }}>Loading transactions…</div>;
  if (error) return <div style={{ padding: "12px 0", textAlign: "center", fontSize: 12 }}>
    <div style={{ color: T.red, marginBottom: 6 }}>Failed to load ({error})</div>
    <button onClick={(e) => { e.stopPropagation(); txDetailCache.delete(cacheKey); setError(null); setRetryTick(t => t + 1); }} style={{ padding: "4px 12px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.card, color: T.textSec, fontSize: 11, cursor: "pointer" }}>Try again</button>
  </div>;
  if (!data) return null;

  const thisLabel = isCreditCard ? "This cycle" : "This month";
  const lastLabel = isCreditCard ? "Last cycle" : "Last month";

  // For savings: current balance applies at end of "this" period (today).
  // Balance at end of "last" period = currentBalance - net change during "this" period.
  const thisAll = [...(data.this?.own || []), ...(data.this?.mummy || []), ...(data.this?.ff || []), ...(data.this?.refunds || [])];
  const thisNet = thisAll.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const balAtEndOfLast = (currentBalance || 0) - thisNet;

  return <div style={{ marginTop: 12, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
    <TxSection title={thisLabel} report={data.this} isCreditCard={isCreditCard} endingBalance={currentBalance} T={T} />
    <div style={{ height: 12 }} />
    <TxSection title={lastLabel} report={data.last} isCreditCard={isCreditCard} endingBalance={balAtEndOfLast} T={T} />
  </div>;
}

// ─── BANK STYLING ─────────────────────────────────────────────────
// Maps account name → bank identity (color gradient + accent + label)
// All colors are official brand hex codes from each bank's guidelines.
// theme = "dark" (default) or "light" — returns theme-appropriate gradient.
function bankStyle(accountName, theme) {
  const isLight = theme === "light";
  const n = String(accountName || "").toLowerCase();

  // Per-bank palette. For each bank:
  //   darkFrom/darkTo = gradient stops in dark mode (deep, near-black with bank tint)
  //   lightFrom/lightTo = gradient stops in light mode (pale tint on white)
  //   accent, accent2 = the accent bar colors (same for both themes)
  const banks = [
    { match: "icici", label: "ICICI", prefix: "ICICI",
      darkFrom: "#4a1214", darkTo: "#140404",
      lightFrom: "#FFF0E8", lightTo: "#FFFAF5",
      accent: "#F99D27", accent2: null },
    { match: "axis", label: "AXIS", prefix: "Axis",
      darkFrom: "#3d0820", darkTo: "#120308",
      lightFrom: "#FDF0F5", lightTo: "#FFF9FB",
      accent: "#97144D", accent2: null },
    { match: "au ", label: "AU BANK", prefix: "AU Bank",
      darkFrom: "#2a0a3a", darkTo: "#100318",
      lightFrom: "#FFF4E8", lightTo: "#FAF0FF",
      accent: "#F58220", accent2: "#7B2CBF" },
    { match: "hdfc", label: "HDFC", prefix: "HDFC",
      darkFrom: "#001f3a", darkTo: "#000814",
      lightFrom: "#E8F0FA", lightTo: "#F8FBFE",
      accent: "#004C8F", accent2: "#ED232A" },
    { match: "sbi", label: "SBI", prefix: "SBI",
      darkFrom: "#1a1558", darkTo: "#0a0824",
      lightFrom: "#EDE8FA", lightTo: "#F8F6FE",
      accent: "#00B5EF", accent2: null },
    { match: "federal", label: "FEDERAL", prefix: "Federal",
      darkFrom: "#001d3a", darkTo: "#000814",
      lightFrom: "#E8F0F8", lightTo: "#FAFDFF",
      accent: "#F7A800", accent2: null },
    { match: "indusind", label: "INDUSIND", prefix: "IndusInd",
      darkFrom: "#3a0e10", darkTo: "#140404",
      lightFrom: "#FDEEEE", lightTo: "#FFF9F9",
      accent: "#98272A", accent2: null },
    { match: "standard chartered", label: "STD CHARTERED", prefix: "SC",
      darkFrom: "#061e3f", darkTo: "#02081a",
      lightFrom: "#E8F4FA", lightTo: "#F8FDFE",
      accent: "#0D4EA6", accent2: "#00A896" },
    { match: "kotak", label: "KOTAK", prefix: "Kotak",
      darkFrom: "#001a3d", darkTo: "#000814",
      lightFrom: "#EBF0F8", lightTo: "#F9FBFE",
      accent: "#ED1C24", accent2: null },
    { match: "rbl", label: "RBL", prefix: "RBL",
      darkFrom: "#3d0810", darkTo: "#14030a",
      lightFrom: "#FCEAEC", lightTo: "#FFF8F9",
      accent: "#ED1C24", accent2: "#21317D" },
    { match: "onecard", label: "ONECARD", prefix: "",
      darkFrom: "#1c1c1c", darkTo: "#050505",
      lightFrom: "#F0F0F0", lightTo: "#FAFAFA",
      accent: "#8a8a8a", accent2: null },
  ];

  for (const b of banks) {
    if (n.includes(b.match) || (b.match === "sc" && (n === "sc" || n.startsWith("sc ")))) {
      return {
        label: b.label,
        short: b.prefix ? stripPrefix(accountName, b.prefix) : accountName,
        bgFrom: isLight ? b.lightFrom : b.darkFrom,
        bgTo: isLight ? b.lightTo : b.darkTo,
        accent: b.accent,
        accent2: b.accent2,
        // Text colors — white on dark, near-black on light
        textPrimary: isLight ? "#111" : "#fff",
        textLabel: isLight ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.5)",
        textFoot: isLight ? "rgba(0,0,0,0.65)" : "rgba(255,255,255,0.7)",
        textFootMuted: isLight ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.6)",
        divider: isLight ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.08)",
        border: isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.06)",
      };
    }
  }
  // Fallback — neutral with gold accent
  return {
    label: "", short: accountName,
    bgFrom: isLight ? "#f5f5f0" : "#0a0a0a",
    bgTo: isLight ? "#ffffff" : "#000000",
    accent: "#d4b65a", accent2: null,
    textPrimary: isLight ? "#111" : "#fff",
    textLabel: isLight ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.5)",
    textFoot: isLight ? "rgba(0,0,0,0.65)" : "rgba(255,255,255,0.7)",
    textFootMuted: isLight ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.6)",
    divider: isLight ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.08)",
    border: isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.06)",
  };
}

function stripPrefix(name, prefix) {
  if (!name) return name;
  const p = prefix.toLowerCase();
  const n = name.toLowerCase();
  let result = name;
  if (n.startsWith(p + " ")) result = name.slice(prefix.length + 1).trim();
  else if (n.startsWith(p)) result = name.slice(prefix.length).trim();
  // If stripping leaves just "Bank" (or something empty/too short), keep the full name
  const r = result.toLowerCase().trim();
  if (!r || r === "bank" || r === "card" || result.length < 3) return name;
  return result;
}

// ─── SUMMARY ROW COMPONENTS (kept outside SummaryScreen so identity is stable) ─
function CardRow({ card, i, isExpanded, onToggle, T, theme }) {
  const hasDue = card.dueDate && card.dueDate !== "";
  const isOverdue = hasDue && card.dueIn !== null && card.dueIn < 0;
  const style = bankStyle(card.name, theme);
  const accentGradient = style.accent2
    ? `linear-gradient(90deg, ${style.accent}, ${style.accent2}, transparent)`
    : `linear-gradient(90deg, ${style.accent}, transparent)`;

  return <FadeIn delay={i * 50}><div onClick={() => onToggle(card.name)} style={{
    position: "relative",
    borderRadius: 18,
    padding: "18px 20px 16px",
    marginBottom: 12,
    overflow: "hidden",
    cursor: "pointer",
    transition: "transform 0.15s ease",
    border: `1px solid ${isOverdue ? T.redBg : style.border}`,
    background: `linear-gradient(135deg, ${style.bgFrom} 0%, ${style.bgTo} 100%)`,
    boxShadow: theme === "light" ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
  }}>
    {/* Top accent bar */}
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accentGradient, opacity: 0.9 }} />

    {/* Header row */}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: style.textLabel, fontWeight: 600, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 8, display: "inline-block", transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
          {style.label || displayName(card)}
          {isOverdue && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: T.redBg, color: T.red, fontWeight: 600, letterSpacing: 1 }}>OVERDUE</span>}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: style.textPrimary, letterSpacing: "-0.3px", lineHeight: 1.15 }}>
          {style.short}
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: style.textLabel, fontWeight: 600 }}>Outstanding</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.accent, marginTop: 2, letterSpacing: "-0.5px" }}>{fmtShort(card.outstanding)}</div>
      </div>
    </div>

    {/* Footer */}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, paddingTop: 12, borderTop: `1px solid ${style.divider}`, gap: 8 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: isOverdue ? T.red : style.textFoot, fontWeight: 500, flexShrink: 0 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: isOverdue ? T.red : style.accent, display: "inline-block" }} />
        {hasDue ? `${card.dueDate} · ${card.dueIn > 0 ? card.dueIn + "d" : card.dueIn === 0 ? "Today" : Math.abs(card.dueIn) + "d ago"}` : "— no due —"}
      </div>
      <div style={{ fontSize: 11, color: style.textFootMuted, fontWeight: 500, textAlign: "right", minWidth: 0 }}>
        {(() => {
          const due = card.lastBill || 0;
          const cycle = card.thisCycleSpend || 0;
          if (due > 0 && cycle > 0) {
            return <>
              <span style={{ color: T.accent, fontWeight: 600 }}>Due {fmtShort(due)}</span>
              <span style={{ opacity: 0.5, margin: "0 6px" }}>·</span>
              <span>Cycle {fmtShort(cycle)}</span>
            </>;
          }
          if (due > 0) return <span style={{ color: T.accent, fontWeight: 600 }}>Due {fmtShort(due)}</span>;
          if (cycle > 0) return <span>Cycle {fmtShort(cycle)}</span>;
          return "No activity";
        })()}
      </div>
    </div>

    {isExpanded && <>
      <div style={{ display: "flex", gap: 12, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${style.divider}`, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: T.pink }}>Mummy: {fmtFull(card.thisCycle?.mummy || 0)}</span>
        <span style={{ fontSize: 11, color: T.blue }}>F&F: {fmtFull(card.thisCycle?.ff || 0)}</span>
        <span style={{ fontSize: 11, color: T.green, marginLeft: "auto", fontWeight: 600 }}>You: {fmtFull(card.thisCycle?.you || 0)}</span>
      </div>
      <div onClick={e => e.stopPropagation()}>
        <CardTransactionDetail accountName={card.name} isCreditCard={true} T={T} />
      </div>
    </>}
  </div></FadeIn>;
}

function SavRow({ s, i, isExpanded, onToggle, T, theme }) {
  const style = bankStyle(s.name, theme);
  const accentGradient = style.accent2
    ? `linear-gradient(90deg, ${style.accent}, ${style.accent2}, transparent)`
    : `linear-gradient(90deg, ${style.accent}, transparent)`;

  return <FadeIn delay={i * 50 + 300}><div onClick={() => onToggle(s.name)} style={{
    position: "relative",
    borderRadius: 18,
    padding: "16px 20px 14px",
    marginBottom: 12,
    overflow: "hidden",
    cursor: "pointer",
    transition: "transform 0.15s ease",
    border: `1px solid ${style.border}`,
    background: `linear-gradient(135deg, ${style.bgFrom} 0%, ${style.bgTo} 100%)`,
    boxShadow: theme === "light" ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
  }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accentGradient, opacity: 0.9 }} />

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: style.textLabel, fontWeight: 600, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 8, display: "inline-block", transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
          {style.label || displayName(s)}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: style.textPrimary, letterSpacing: "-0.3px", lineHeight: 1.15 }}>
          {style.short}
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: style.textLabel, fontWeight: 600 }}>Balance</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.green, marginTop: 2, letterSpacing: "-0.5px" }}>{fmtShort(s.balance || 0)}</div>
      </div>
    </div>

    <div style={{ display: "flex", gap: 12, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${style.divider}`, flexWrap: "wrap", fontSize: 11 }}>
      <span style={{ color: style.textFootMuted }}>Spent: {fmtFull(s.thisMonth?.expenses || 0)}</span>
      <span style={{ color: T.pink }}>M: {fmtFull(s.thisMonth?.mummy || 0)}</span>
      <span style={{ color: T.blue }}>F&F: {fmtFull(s.thisMonth?.ff || 0)}</span>
      <span style={{ color: T.green, marginLeft: "auto", fontWeight: 600 }}>You: {fmtFull(s.thisMonth?.you || 0)}</span>
    </div>

    {isExpanded && <div onClick={e => e.stopPropagation()}>
      <CardTransactionDetail accountName={s.name} isCreditCard={false} currentBalance={s.balance || 0} T={T} />
    </div>}
  </div></FadeIn>;
}

// ─── SUMMARY SCREEN ────────────────────────────────────────────────
function SummaryScreen({ summaryData, loading, onRefresh, T, theme }) {
  const [expanded, setExpanded] = useState(new Set());
  const [totalOpen, setTotalOpen] = useState(false);
  const toggleExpand = useCallback((name) => setExpanded(p => { const n = new Set(p); if (n.has(name)) n.delete(name); else n.add(name); return n; }), []);
  if (loading) return <LoadingState T={T} />;
  if (!summaryData) return <EmptyState message="Pull data to see summary" onAction={onRefresh} actionLabel="Refresh" T={T} />;
  const { creditCards = [], savings = [], totalOutstanding = 0, missingCategory = 0 } = summaryData;

  // Breakdown: sum last bills (unpaid bills due soon) vs this cycle spend across all CCs
  const totalDue = creditCards.reduce((s, c) => s + (c.lastBill || 0), 0);
  const totalCycle = creditCards.reduce((s, c) => s + (c.thisCycleSpend || 0), 0);

  return <div style={{ padding: "0 10px 100px", overflowY: "auto", height: "100%" }}>
    <FadeIn delay={0}>
      <div
        onClick={() => setTotalOpen(o => !o)}
        style={{
          textAlign: "center",
          padding: "20px 24px 20px",
          marginTop: 12,
          marginBottom: 16,
          borderRadius: 20,
          background: `linear-gradient(135deg, ${T.accent}15, ${T.accent}05)`,
          border: `1px solid ${T.accent}30`,
          position: "relative",
          overflow: "hidden",
          cursor: "pointer",
          transition: "transform 0.15s",
        }}
      >
        <div style={{ position: "absolute", top: -50, right: -50, width: 150, height: 150, borderRadius: "50%", background: `radial-gradient(circle, ${T.accent}20, transparent)`, pointerEvents: "none" }} />
        <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: 3, textTransform: "uppercase", fontWeight: 500 }}>Total Outstanding</div>
        <div style={{ fontSize: 42, fontWeight: 800, background: T.accentGrad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginTop: 6, letterSpacing: -1 }}>{fmtFull(totalOutstanding)}</div>
        <div style={{ fontSize: 10, color: T.textDim, marginTop: 4, letterSpacing: 1 }}>
          <span style={{ display: "inline-block", transform: totalOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", marginRight: 4 }}>▶</span>
          {totalOpen ? "Hide breakdown" : "Tap for breakdown"}
        </div>

        {totalOpen && (
          <div style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: `1px solid ${T.accent}30`,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            textAlign: "left",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600 }}>Bills due soon</div>
                <div style={{ fontSize: 11, color: T.textDim, marginTop: 1 }}>Unpaid bills pending due date</div>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.accent }}>{fmtFull(totalDue)}</div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600 }}>This cycle spend</div>
                <div style={{ fontSize: 11, color: T.textDim, marginTop: 1 }}>Accumulating, not yet billed</div>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{fmtFull(totalCycle)}</div>
            </div>
          </div>
        )}
      </div>
    </FadeIn>
    {missingCategory > 0 && <FadeIn delay={30}><div style={{ background: "#78350f20", border: "1px solid #f59e0b33", borderRadius: 12, padding: "10px 16px", marginBottom: 16, fontSize: 13, color: T.yellow }}>⚠ {missingCategory} transaction{missingCategory > 1 ? "s" : ""} missing category</div></FadeIn>}
    {creditCards.length > 0 && <><div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10, color: T.accent }}>CREDIT CARDS ({creditCards.length})</div>{creditCards.map((c, i) => <CardRow key={c.name} card={c} i={i} isExpanded={expanded.has(c.name)} onToggle={toggleExpand} T={T} theme={theme} />)}</>}
    {savings.length > 0 && <><div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10, marginTop: 20, color: T.blue }}>SAVINGS — THIS MONTH</div>
      {savings.map((s, i) => <SavRow key={s.name} s={s} i={i} isExpanded={expanded.has(s.name)} onToggle={toggleExpand} T={T} theme={theme} />)}
    </>}
  </div>;
}

// ─── REPORTS SCREEN (card name, latest first, You/Mummy/F&F) ──────
function ReportsScreen({ T }) {
  const [reportType, setReportType] = useState("combined");
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState(new Set(["own", "mummy", "ff", "refunds"]));
  const [compact, setCompact] = useState(false);
  const [search, setSearch] = useState("");
  const reportOptions = [
    { id: "cc-this", label: "CC This Cycle" }, { id: "cc-last", label: "CC Last Cycle" },
    { id: "sav-this", label: "Savings This Month" }, { id: "sav-last", label: "Savings Last Month" },
    { id: "combined", label: "Combined" },
  ];
  const filterItems = [
    { id: "own", label: "You", color: T.green },
    { id: "mummy", label: "Mummy", color: T.pink },
    { id: "ff", label: "F&F", color: T.blue },
    { id: "refunds", label: "Refunds", color: T.green },
  ];
  const toggleFilter = (id) => {
    setActiveFilters(prev => {
      if (prev.size === 4) {
        // Was showing all — switch to just this one
        return new Set([id]);
      }
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      if (next.size === 0) return new Set(["own", "mummy", "ff", "refunds"]);
      return next;
    });
  };
  const isAll = activeFilters.size === 4;
  const selectAll = () => setActiveFilters(new Set(["own", "mummy", "ff", "refunds"]));

  const fetchReport = useCallback(async (type) => {
    setReportLoading(true);
    try { setReportData(await api.get(`/api/report/${type}`)); } catch (e) { console.error(e); }
    setReportLoading(false);
  }, []);
  useEffect(() => { fetchReport(reportType); setSearch(""); }, [reportType, fetchReport]);

  // Parse timestamp: "2026-04-10T17:31:42" or "2026-04-10 17:31:42" → ms
  const parseTS = (s) => {
    if (!s) return 0;
    const str = String(s).replace("T", " ");
    // Manual parse: YYYY-MM-DD HH:MM:SS
    const m = str.match(/(\d{4})-(\d{2})-(\d{2})\s*(\d{2})?:?(\d{2})?:?(\d{2})?/);
    if (m) return new Date(+m[1], +m[2]-1, +m[3], +(m[4]||0), +(m[5]||0), +(m[6]||0)).getTime();
    return 0;
  };
  // Parse date: "10-Apr-2026" → ms
  const parseD = (s) => {
    if (!s) return 0;
    const mo = {jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11};
    const p = String(s).split("-");
    if (p.length === 3) { const d=parseInt(p[0]), m=mo[p[1].toLowerCase().slice(0,3)], y=parseInt(p[2]); if(!isNaN(d)&&m!==undefined&&!isNaN(y)) return new Date(y,m,d).getTime(); }
    return 0;
  };

  // Build combined filtered + sorted list
  const allTxs = reportData ? (() => {
    let txs = [];
    if (activeFilters.has("own")) txs.push(...(reportData.own || []).map(t => ({ ...t, _type: "own" })));
    if (activeFilters.has("mummy")) txs.push(...(reportData.mummy || []).map(t => ({ ...t, _type: "mummy" })));
    if (activeFilters.has("ff")) txs.push(...(reportData.ff || []).map(t => ({ ...t, _type: "ff" })));
    if (activeFilters.has("refunds")) txs.push(...(reportData.refunds || []).map(t => ({ ...t, _type: "refund" })));
    // Sort: primary by date (newest day first), then by timestamp for same-day ordering
    return txs.sort((a, b) => {
      const dA = parseD(a.date), dB = parseD(b.date);
      if (dA !== dB) return dB - dA;
      // Same day — use timestamp as tiebreaker
      return parseTS(b.timestamp) - parseTS(a.timestamp);
    });
  })() : [];

  // Apply search filter — matches note (case-insensitive) or exact amount
  const searchedTxs = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return allTxs;
    // Try to parse as a number for amount matching
    const num = parseFloat(q.replace(/[,₹\s]/g, ""));
    const isNum = !isNaN(num) && /^[\d.,₹\s]+$/.test(q);
    return allTxs.filter(tx => {
      const note = String(tx.note || "").toLowerCase();
      if (note.includes(q)) return true;
      if (isNum) {
        // Match on absolute amount (so "500" finds both -500 and +500)
        const amt = Math.abs(Number(tx.amount) || 0);
        if (amt === num) return true;
        // Also partial number match in the string form (e.g. "15" finds 1500, 150)
        if (String(amt).includes(String(num))) return true;
      }
      return false;
    });
  })();

  const typeColor = (t) => t === "mummy" ? T.pink : t === "ff" ? T.blue : t === "refund" ? T.green : "";
  const typeLabel = (t) => t === "mummy" ? "Mummy" : t === "ff" ? "F&F" : t === "refund" ? "Refund" : "";

  // Date label: "Today", "Yesterday", or "12 Apr 2026"
  const dateLabel = (dateStr) => {
    const ms = parseD(dateStr);
    if (!ms) return dateStr;
    const today = new Date(); today.setHours(0,0,0,0);
    const tt = today.getTime();
    const diff = Math.round((tt - ms) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    const d = new Date(ms);
    return d.getDate() + " " + ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()] + " " + d.getFullYear();
  };

  const TxRow = ({ tx, compact }) => compact ?
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${T.bg}`, gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 13, color: T.textSec, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.note || "No note"}</span>
        {tx._type && tx._type !== "own" && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: typeColor(tx._type) + "20", color: typeColor(tx._type), fontWeight: 600, flexShrink: 0 }}>{typeLabel(tx._type)[0]}</span>}
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color: tx.amount > 0 ? T.green : T.text, whiteSpace: "nowrap" }}>{tx.amount > 0 ? "+" : ""}{fmtFull(tx.amount)}</span>
    </div>
    : <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${T.bg}` }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 14, color: T.textSec }}>{tx.note || "No note"}</span>
          {tx._type && tx._type !== "own" && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: typeColor(tx._type) + "20", color: typeColor(tx._type), fontWeight: 600 }}>{typeLabel(tx._type)}</span>}
        </div>
        <div style={{ fontSize: 11, color: T.textMuted }}>{tx.account}</div>
        {tx.category && <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>{tx.category}{tx.subcategory ? " → " + tx.subcategory : ""}</div>}
      </div>
      <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 12, color: tx.amount > 0 ? T.green : T.text, whiteSpace: "nowrap" }}>{tx.amount > 0 ? "+" : ""}{fmtFull(tx.amount)}</span>
    </div>;

  // Group txs by date for separator headers
  const groupedTxs = (() => {
    const groups = [];
    let cur = null;
    searchedTxs.forEach(tx => {
      if (!cur || cur.date !== tx.date) {
        cur = { date: tx.date, label: dateLabel(tx.date), items: [] };
        groups.push(cur);
      }
      cur.items.push(tx);
    });
    return groups;
  })();

  return <div style={{ padding: "0 20px 100px", overflowY: "auto", height: "100%" }}>
    {/* Search bar */}
    <FadeIn delay={20}><div style={{ position: "relative", marginTop: 12, marginBottom: 4 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2.5" strokeLinecap="round" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      <input
        type="text"
        inputMode="search"
        placeholder="Search note or amount…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 36px 10px 38px",
          borderRadius: 20,
          border: `1px solid ${T.border}`,
          background: T.card,
          color: T.text,
          fontSize: 13,
          outline: "none",
          fontFamily: "inherit",
        }}
      />
      {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer", padding: "4px 10px", lineHeight: 1 }}>×</button>}
    </div></FadeIn>
    {/* Report type pills */}
    <FadeIn delay={50}><div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 16, marginTop: 12, WebkitOverflowScrolling: "touch" }}>
      {reportOptions.map(r => <button key={r.id} onClick={() => setReportType(r.id)} style={{ padding: "8px 14px", borderRadius: 20, border: `1px solid ${reportType === r.id ? T.accent : T.border}`, background: reportType === r.id ? T.accentBg : T.card, color: reportType === r.id ? T.accent : T.textMuted, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap", fontWeight: reportType === r.id ? 600 : 400, transition: "all 0.2s" }}>{r.label}</button>)}
    </div></FadeIn>
    {reportLoading ? <LoadingState T={T} /> : reportData ? <>
      {/* Summary box */}
      <FadeIn delay={100}><div style={{ background: T.card, borderRadius: 14, padding: 16, marginBottom: 16, border: `1px solid ${T.border}` }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><span style={{ fontSize: 11, color: T.textMuted }}>Total Spend</span><div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{fmtFull(reportData.splits?.expenses || 0)}</div></div>
          <div><span style={{ fontSize: 11, color: T.textMuted }}>Refunds</span><div style={{ fontSize: 18, fontWeight: 700, color: T.green }}>{fmtFull(reportData.splits?.refunds || 0)}</div></div>
          <div><span style={{ fontSize: 11, color: T.textMuted }}>Net Spend</span><div style={{ fontSize: 18, fontWeight: 700, color: T.accent }}>{fmtFull(reportData.splits?.net || 0)}</div></div>
          <div><span style={{ fontSize: 11, color: T.textMuted }}>Your Share</span><div style={{ fontSize: 18, fontWeight: 700, color: T.green }}>{fmtFull(reportData.splits?.you || 0)}</div></div>
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 12, color: T.pink }}>Mummy: {fmtFull(reportData.splits?.mummy || 0)}</span>
          <span style={{ fontSize: 12, color: T.blue }}>F&F: {fmtFull(reportData.splits?.ff || 0)}</span>
        </div>
      </div></FadeIn>
      {/* Filter chips */}
      <FadeIn delay={150}><div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={selectAll} style={{ padding: "7px 16px", borderRadius: 20, border: `1px solid ${isAll ? T.accent : T.border}`, background: isAll ? T.accentBg : "transparent", color: isAll ? T.accent : T.textDim, fontSize: 12, cursor: "pointer", fontWeight: isAll ? 600 : 400, transition: "all 0.2s" }}>All</button>
        {filterItems.map(f => {
          const active = activeFilters.has(f.id) && !isAll;
          return <button key={f.id} onClick={() => toggleFilter(f.id)} style={{ padding: "7px 16px", borderRadius: 20, border: `1px solid ${active ? f.color : T.border}`, background: active ? f.color + "20" : "transparent", color: active ? f.color : T.textDim, fontSize: 12, cursor: "pointer", fontWeight: active ? 600 : 400, transition: "all 0.2s" }}>{f.label}</button>;
        })}
      </div></FadeIn>
      {/* Combined transaction list */}
      <FadeIn delay={200}>
        {searchedTxs.length > 0 ? <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: T.textMuted }}>
              TRANSACTIONS ({searchedTxs.length}{search && allTxs.length !== searchedTxs.length ? ` of ${allTxs.length}` : ""})
            </div>
            <button onClick={() => setCompact(c => !c)} style={{ padding: "4px 12px", borderRadius: 12, border: `1px solid ${T.border}`, background: "transparent", color: T.textMuted, fontSize: 10, cursor: "pointer", letterSpacing: 1, textTransform: "uppercase" }}>{compact ? "Detailed" : "Compact"}</button>
          </div>
          {groupedTxs.map((group, gi) => <div key={gi} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: T.accent, padding: "8px 4px 4px", fontWeight: 600 }}>{group.label}</div>
            <div style={{ background: T.card, borderRadius: 12, padding: "4px 16px" }}>
              {group.items.map((tx, i) => <TxRow key={i} tx={tx} compact={compact} />)}
            </div>
          </div>)}
        </div> : <div style={{ textAlign: "center", padding: 40, color: T.textDim }}>
          {search ? `No matches for "${search}"` : "No transactions found"}
        </div>}
      </FadeIn>
    </> : null}
  </div>;
}

// ─── CHARTS SCREEN ─────────────────────────────────────────────────
// Palette shared by both charts — works on dark and light
const CHART_PALETTE = [
  "#F99D27", "#97144D", "#F58220", "#004C8F", "#00B5EF",
  "#F7A800", "#98272A", "#00A896", "#ED1C24", "#21317D",
  "#7B2CBF", "#5eead4", "#f472b6", "#60a5fa", "#4ade80",
];

// Chart report type options — mirrors Reports tab
const CHART_REPORT_OPTIONS = [
  { id: "combined", label: "Combined" },
  { id: "cc-this", label: "CC This Cycle" },
  { id: "cc-last", label: "CC Last Cycle" },
  { id: "sav-this", label: "Savings This Month" },
  { id: "sav-last", label: "Savings Last Month" },
];

// Module-level cache for chart data, keyed by report type. TTL 2 min.
// Cleared from Settings → Refresh Data.
const chartCache = new Map();
const CHART_CACHE_TTL = 2 * 60 * 1000;
function getChartCache(type) {
  const hit = chartCache.get(type);
  if (!hit) return null;
  if (Date.now() - hit.fetchedAt > CHART_CACHE_TTL) {
    chartCache.delete(type);
    return null;
  }
  return hit.data;
}
function setChartCache(type, data) {
  chartCache.set(type, { data, fetchedAt: Date.now() });
}
function clearChartCache() { chartCache.clear(); }

// Transform /api/report/<type> response into the shape chart components expect:
// { monthLabel, total, categories: [{category, amount, subcategories: [{subcategory, amount, transactions: [...]}]}], labels: [{label, amount, transactions: [...]}] }
function transformReportToChartData(reportData, reportTypeLabel) {
  if (!reportData) return null;

  // Collect all spend transactions (own + mummy + ff, excluding refunds which are credits)
  // Attach the _type so we can show M/F&F tags like the Reports tab does
  const spendTxs = [];
  (reportData.own || []).forEach(t => spendTxs.push({ ...t, _type: "own" }));
  (reportData.mummy || []).forEach(t => spendTxs.push({ ...t, _type: "mummy" }));
  (reportData.ff || []).forEach(t => spendTxs.push({ ...t, _type: "ff" }));
  // Note: refunds excluded — charts show spend, not income

  // Shape each tx for chart consumption (same shape the old /api/charts endpoint produced)
  const shape = (t) => ({
    date: t.date || "",
    note: t.note || "",
    amount: Number(t.amount) || 0,
    account: t.account || "",
    category: String(t.category || "").trim(),
    subcategory: String(t.subcategory || "").trim(),
    labels: String(t.labels || ""),
    timestamp: t.timestamp || "",
    _type: t._type,
  });

  // Helper: parse "10-Apr-2026" → ms for sorting
  const parseD = (s) => {
    if (!s) return 0;
    const mo = {jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11};
    const p = String(s).split("-");
    if (p.length === 3) { const d=parseInt(p[0]), m=mo[p[1].toLowerCase().slice(0,3)], y=parseInt(p[2]); if(!isNaN(d)&&m!==undefined&&!isNaN(y)) return new Date(y,m,d).getTime(); }
    return 0;
  };

  // ─── Build category tree ────────────────────────────────────
  const catMap = {};
  let total = 0;
  for (const rawT of spendTxs) {
    const t = shape(rawT);
    const cat = t.category || "Uncategorised";
    const sub = t.subcategory || "Other";
    const amt = Math.abs(t.amount);
    total += amt;

    if (!catMap[cat]) catMap[cat] = { amount: 0, subs: {} };
    catMap[cat].amount += amt;

    if (!catMap[cat].subs[sub]) catMap[cat].subs[sub] = { amount: 0, txns: [] };
    catMap[cat].subs[sub].amount += amt;
    catMap[cat].subs[sub].txns.push(t);
  }

  const categories = Object.entries(catMap)
    .sort((a, b) => b[1].amount - a[1].amount)
    .map(([catName, catData]) => ({
      category: catName,
      amount: Math.round(catData.amount * 100) / 100,
      subcategories: Object.entries(catData.subs)
        .sort((a, b) => b[1].amount - a[1].amount)
        .map(([subName, subData]) => ({
          subcategory: subName,
          amount: Math.round(subData.amount * 100) / 100,
          transactions: subData.txns.sort((a, b) => parseD(b.date) - parseD(a.date)),
        })),
    }));

  // ─── Build labels bucket ────────────────────────────────────
  const labelMap = {};
  for (const rawT of spendTxs) {
    const t = shape(rawT);
    const labelsList = String(t.labels || "")
      .split(",")
      .map(l => l.trim())
      .filter(Boolean);
    const effectiveLabels = labelsList.length > 0 ? labelsList : ["Unlabelled"];
    const amt = Math.abs(t.amount);

    for (const lbl of effectiveLabels) {
      if (!labelMap[lbl]) labelMap[lbl] = { amount: 0, txns: [] };
      labelMap[lbl].amount += amt;
      labelMap[lbl].txns.push(t);
    }
  }

  const labels = Object.entries(labelMap)
    .sort((a, b) => b[1].amount - a[1].amount)
    .map(([lblName, lblData]) => ({
      label: lblName,
      amount: Math.round(lblData.amount * 100) / 100,
      transactions: lblData.txns.sort((a, b) => parseD(b.date) - parseD(a.date)),
    }));

  return {
    monthLabel: reportTypeLabel,
    total: Math.round(total * 100) / 100,
    categories,
    labels,
  };
}

function ChartsScreen({ T, theme }) {
  const [catType, setCatType] = useState("combined");
  const [labelType, setLabelType] = useState("combined");
  const [catData, setCatData] = useState(null);
  const [labelData, setLabelData] = useState(null);
  const [catLoading, setCatLoading] = useState(true);
  const [labelLoading, setLabelLoading] = useState(true);
  const [catError, setCatError] = useState(null);
  const [labelError, setLabelError] = useState(null);

  const labelFor = (id) => CHART_REPORT_OPTIONS.find(o => o.id === id)?.label || id;

  // Fetch for categories section — independent, cache-aware
  useEffect(() => {
    const cached = getChartCache(catType);
    if (cached) {
      setCatData(cached); setCatLoading(false); setCatError(null);
      return;
    }
    let cancelled = false;
    setCatLoading(true); setCatError(null);
    api.get(`/api/report/${catType}`)
      .then(d => {
        if (cancelled) return;
        const transformed = transformReportToChartData(d, labelFor(catType));
        setChartCache(catType, transformed);
        setCatData(transformed); setCatLoading(false);
      })
      .catch(e => { if (!cancelled) { setCatError(e.message || "Failed"); setCatLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [catType]);

  // Fetch for labels section — independent, cache-aware, does NOT sync with catType
  useEffect(() => {
    const cached = getChartCache(labelType);
    if (cached) {
      setLabelData(cached); setLabelLoading(false); setLabelError(null);
      return;
    }
    let cancelled = false;
    setLabelLoading(true); setLabelError(null);
    api.get(`/api/report/${labelType}`)
      .then(d => {
        if (cancelled) return;
        const transformed = transformReportToChartData(d, labelFor(labelType));
        setChartCache(labelType, transformed);
        setLabelData(transformed); setLabelLoading(false);
      })
      .catch(e => { if (!cancelled) { setLabelError(e.message || "Failed"); setLabelLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [labelType]);

  return <div style={{ padding: "0 16px 100px", overflowY: "auto", height: "100%" }}>
    <CategorySection
      reportType={catType}
      setReportType={setCatType}
      data={catData}
      loading={catLoading}
      error={catError}
      T={T}
    />
    <LabelSection
      reportType={labelType}
      setReportType={setLabelType}
      data={labelData}
      loading={labelLoading}
      error={labelError}
      T={T}
    />
  </div>;
}

// ─── REPORT-TYPE PILLS (horizontal scroll) ──────────────────────
function ReportTypePills({ reportType, setReportType, accentColor, T }) {
  return <div style={{ display: "flex", gap: 6, marginBottom: 12, overflowX: "auto", paddingBottom: 4, WebkitOverflowScrolling: "touch" }}>
    {CHART_REPORT_OPTIONS.map(o => {
      const active = o.id === reportType;
      return <button key={o.id} onClick={() => setReportType(o.id)} style={{
        padding: "6px 14px",
        borderRadius: 20,
        border: `1px solid ${active ? accentColor : T.border}`,
        background: active ? accentColor + "22" : "transparent",
        color: active ? accentColor : T.textMuted,
        fontSize: 12,
        cursor: "pointer",
        fontWeight: active ? 600 : 400,
        transition: "all 0.2s",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}>{o.label}</button>;
    })}
  </div>;
}

// ─── CATEGORY SECTION ───────────────────────────────────────────
function CategorySection({ reportType, setReportType, data, loading, error, T }) {
  const [expandedCat, setExpandedCat] = useState(null);
  const [expandedSub, setExpandedSub] = useState(null);

  // Reset drill state when report type changes
  useEffect(() => { setExpandedCat(null); setExpandedSub(null); }, [reportType]);

  const total = data?.total || 0;
  const categories = data?.categories || [];

  return <FadeIn delay={0}>
    <div style={{ marginTop: 16, marginBottom: 8, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: T.accent, fontWeight: 600 }}>
      Spend by category
    </div>
    <ReportTypePills reportType={reportType} setReportType={setReportType} accentColor={T.accent} T={T} />

    {loading ? (
      <div style={{ padding: 40, textAlign: "center", color: T.textDim, fontSize: 13 }}>Loading…</div>
    ) : error ? (
      <div style={{ padding: 20, textAlign: "center", color: T.red, fontSize: 13 }}>Failed: {error}</div>
    ) : categories.length === 0 ? (
      <div style={{ padding: 40, textAlign: "center", color: T.textDim, fontSize: 13 }}>No spending for {data?.monthLabel || "this period"}</div>
    ) : (
      <>
        <div style={{ fontSize: 32, fontWeight: 800, color: T.text, letterSpacing: "-1px", marginBottom: 4 }}>
          {fmtFull(total)}
        </div>
        <div style={{ fontSize: 11, color: T.textDim, marginBottom: 16 }}>{data.monthLabel}</div>

        <div style={{ background: T.card, borderRadius: 14, padding: 16, marginBottom: 24, border: `1px solid ${T.border}` }}>
          {categories.map((c, i) => (
            <CategoryRow
              key={c.category}
              cat={c}
              index={i}
              total={total}
              isExpanded={expandedCat === c.category}
              onToggle={() => { setExpandedCat(expandedCat === c.category ? null : c.category); setExpandedSub(null); }}
              expandedSub={expandedSub}
              setExpandedSub={setExpandedSub}
              isLast={i === categories.length - 1}
              T={T}
            />
          ))}
        </div>
      </>
    )}
  </FadeIn>;
}

function CategoryRow({ cat, index, total, isExpanded, onToggle, expandedSub, setExpandedSub, isLast, T }) {
  const pct = total > 0 ? (cat.amount / total) * 100 : 0;
  const color = CHART_PALETTE[index % CHART_PALETTE.length];

  return <div style={{ marginBottom: isLast && !isExpanded ? 0 : 12 }}>
    <div onClick={onToggle} style={{ cursor: "pointer" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 13, alignItems: "center" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.text, fontWeight: 500 }}>
          <span style={{ fontSize: 8, display: "inline-block", transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", color: T.textDim }}>▶</span>
          {cat.category}
        </span>
        <span style={{ color: T.textSec, fontWeight: 600 }}>
          {fmtFull(cat.amount)} <span style={{ color: T.textDim, fontSize: 11 }}>({pct.toFixed(0)}%)</span>
        </span>
      </div>
      <div style={{ width: "100%", height: 6, background: T.bg, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.5s ease" }} />
      </div>
    </div>

    {isExpanded && (
      <div style={{ marginTop: 10, marginLeft: 14, paddingLeft: 10, borderLeft: `1px solid ${T.border}` }}>
        {cat.subcategories.map((sub, j) => (
          <SubcategoryRow
            key={sub.subcategory}
            sub={sub}
            parentAmount={cat.amount}
            color={color}
            isExpanded={expandedSub === `${cat.category}::${sub.subcategory}`}
            onToggle={() => setExpandedSub(expandedSub === `${cat.category}::${sub.subcategory}` ? null : `${cat.category}::${sub.subcategory}`)}
            isLast={j === cat.subcategories.length - 1}
            T={T}
          />
        ))}
      </div>
    )}
  </div>;
}

function SubcategoryRow({ sub, parentAmount, color, isExpanded, onToggle, isLast, T }) {
  const pct = parentAmount > 0 ? (sub.amount / parentAmount) * 100 : 0;

  return <div style={{ marginTop: 8, marginBottom: isLast && !isExpanded ? 0 : 8 }}>
    <div onClick={onToggle} style={{ cursor: "pointer" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, fontSize: 12, alignItems: "center" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.textSec }}>
          <span style={{ fontSize: 7, display: "inline-block", transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", color: T.textDim }}>▶</span>
          {sub.subcategory}
        </span>
        <span style={{ color: T.textSec, fontSize: 12 }}>
          {fmtFull(sub.amount)} <span style={{ color: T.textDim, fontSize: 10 }}>({pct.toFixed(0)}%)</span>
        </span>
      </div>
      <div style={{ width: "100%", height: 4, background: T.bg, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, opacity: 0.6, borderRadius: 2 }} />
      </div>
    </div>

    {isExpanded && (
      <div style={{ marginTop: 8, background: T.bg, borderRadius: 8, padding: "4px 10px" }}>
        {sub.transactions.map((tx, k) => <ChartTxRow key={k} tx={tx} isLast={k === sub.transactions.length - 1} T={T} />)}
      </div>
    )}
  </div>;
}

function ChartTxRow({ tx, isLast, T }) {
  const labels = String(tx.labels || "").toLowerCase();
  let tag = null;
  if (labels.includes("mummy") || labels.includes("pappa")) tag = "M";
  else if (labels.includes("friends")) tag = "F&F";

  return <div style={{
    display: "flex", justifyContent: "space-between", gap: 8,
    padding: "6px 4px",
    borderBottom: isLast ? "none" : `1px solid ${T.border}`,
    fontSize: 12,
  }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: T.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {tx.note || "—"}
        {tag && <span style={{ marginLeft: 6, fontSize: 9, padding: "1px 5px", borderRadius: 4, background: tag === "M" ? T.pink + "22" : T.blue + "22", color: tag === "M" ? T.pink : T.blue, fontWeight: 600 }}>{tag}</span>}
      </div>
      <div style={{ color: T.textDim, fontSize: 10, marginTop: 2 }}>{tx.date}{tx.account ? ` · ${tx.account}` : ""}</div>
    </div>
    <div style={{ color: T.text, fontWeight: 600, whiteSpace: "nowrap" }}>{fmtFull(tx.amount)}</div>
  </div>;
}

// ─── LABEL SECTION (donut + drill-down) ─────────────────────────
function LabelSection({ reportType, setReportType, data, loading, error, T }) {
  const [expandedLbl, setExpandedLbl] = useState(null);

  useEffect(() => { setExpandedLbl(null); }, [reportType]);

  const rawLabels = data?.labels || [];
  const total = rawLabels.reduce((s, l) => s + l.amount, 0);

  // Group small slices (<3% of total) into "Other"
  const processed = useMemo(() => {
    if (!rawLabels.length || total === 0) return { slices: [], others: [] };
    const threshold = total * 0.03;
    const big = [];
    const small = [];
    rawLabels.forEach(l => {
      if (l.amount >= threshold) big.push(l); else small.push(l);
    });
    const slices = big.map((l, i) => ({
      ...l, color: CHART_PALETTE[i % CHART_PALETTE.length], key: l.label, isOther: false,
    }));
    if (small.length > 0) {
      const otherTotal = small.reduce((s, l) => s + l.amount, 0);
      // Merge all "Other" transactions
      const otherTxns = [];
      small.forEach(l => otherTxns.push(...l.transactions));
      // Dedupe by timestamp+note+amount
      const seen = new Set();
      const uniqueTxns = otherTxns.filter(t => {
        const k = `${t.timestamp}|${t.note}|${t.amount}`;
        if (seen.has(k)) return false;
        seen.add(k); return true;
      });
      slices.push({
        label: "Other",
        amount: otherTotal,
        transactions: uniqueTxns,
        color: T.textDim,
        key: "__other__",
        isOther: true,
        breakdown: small,
      });
    }
    return { slices, others: small };
  }, [rawLabels, total, T.textDim]);

  return <FadeIn delay={100}>
    <div style={{ marginTop: 8, marginBottom: 8, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: T.blue, fontWeight: 600 }}>
      Spend by label
    </div>
    <ReportTypePills reportType={reportType} setReportType={setReportType} accentColor={T.blue} T={T} />

    {loading ? (
      <div style={{ padding: 40, textAlign: "center", color: T.textDim, fontSize: 13 }}>Loading…</div>
    ) : error ? (
      <div style={{ padding: 20, textAlign: "center", color: T.red, fontSize: 13 }}>Failed: {error}</div>
    ) : processed.slices.length === 0 ? (
      <div style={{ padding: 40, textAlign: "center", color: T.textDim, fontSize: 13 }}>No labelled spending for {data?.monthLabel || "this period"}</div>
    ) : (
      <div style={{ background: T.card, borderRadius: 14, padding: 16, border: `1px solid ${T.border}`, marginBottom: 16 }}>
        {/* Donut */}
        <DonutChart slices={processed.slices} total={total} T={T} />

        {/* Legend as tappable rows */}
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
          {processed.slices.map((slice) => (
            <LabelLegendRow
              key={slice.key}
              slice={slice}
              total={total}
              isExpanded={expandedLbl === slice.key}
              onToggle={() => setExpandedLbl(expandedLbl === slice.key ? null : slice.key)}
              T={T}
            />
          ))}
        </div>
      </div>
    )}
  </FadeIn>;
}

function LabelLegendRow({ slice, total, isExpanded, onToggle, T }) {
  const pct = total > 0 ? (slice.amount / total) * 100 : 0;
  return <div style={{ marginBottom: 8 }}>
    <div onClick={onToggle} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", fontSize: 13 }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8, color: T.text }}>
        <span style={{ fontSize: 8, color: T.textDim, transition: "transform 0.2s", display: "inline-block", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
        <span style={{ width: 10, height: 10, borderRadius: 2, background: slice.color, display: "inline-block" }} />
        <span style={{ fontWeight: 500 }}>{slice.label}</span>
      </span>
      <span style={{ color: T.textSec, fontWeight: 600 }}>
        {fmtFull(slice.amount)} <span style={{ color: T.textDim, fontSize: 11 }}>({pct.toFixed(0)}%)</span>
      </span>
    </div>

    {isExpanded && slice.isOther && slice.breakdown && (
      <div style={{ marginLeft: 20, marginTop: 4, marginBottom: 4, fontSize: 11, color: T.textMuted }}>
        {slice.breakdown.map(b => (
          <div key={b.label} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
            <span>{b.label}</span>
            <span>{fmtFull(b.amount)}</span>
          </div>
        ))}
      </div>
    )}

    {isExpanded && (
      <div style={{ marginTop: 6, marginLeft: 14, paddingLeft: 10, borderLeft: `1px solid ${T.border}` }}>
        <div style={{ background: T.bg, borderRadius: 8, padding: "4px 10px" }}>
          {slice.transactions.length === 0 ? (
            <div style={{ padding: 8, textAlign: "center", color: T.textDim, fontSize: 11 }}>No transactions</div>
          ) : (
            slice.transactions.map((tx, k) => <ChartTxRow key={k} tx={tx} isLast={k === slice.transactions.length - 1} T={T} />)
          )}
        </div>
      </div>
    )}
  </div>;
}

// ─── DONUT CHART (pure SVG, no deps) ─────────────────────────────
function DonutChart({ slices, total, T }) {
  const size = 180;
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = 80;
  const rInner = 50;

  // Build arc paths
  let cumAngle = -Math.PI / 2; // start at top
  const arcs = slices.map((slice) => {
    const angle = total > 0 ? (slice.amount / total) * Math.PI * 2 : 0;
    const startAngle = cumAngle;
    const endAngle = cumAngle + angle;
    cumAngle = endAngle;

    const x1 = cx + rOuter * Math.cos(startAngle);
    const y1 = cy + rOuter * Math.sin(startAngle);
    const x2 = cx + rOuter * Math.cos(endAngle);
    const y2 = cy + rOuter * Math.sin(endAngle);
    const x3 = cx + rInner * Math.cos(endAngle);
    const y3 = cy + rInner * Math.sin(endAngle);
    const x4 = cx + rInner * Math.cos(startAngle);
    const y4 = cy + rInner * Math.sin(startAngle);
    const largeArc = angle > Math.PI ? 1 : 0;

    const path = [
      `M ${x1} ${y1}`,
      `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4}`,
      "Z",
    ].join(" ");

    return { path, color: slice.color, key: slice.key };
  });

  return <div style={{ display: "flex", justifyContent: "center" }}>
    <svg width={size} height={size}>
      {arcs.map(a => <path key={a.key} d={a.path} fill={a.color} opacity={0.9} />)}
      {/* Center total */}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="10" fill={T.textMuted} fontWeight="600" letterSpacing="1">TOTAL</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize="16" fill={T.text} fontWeight="700">
        {total >= 100000 ? "₹" + (total / 100000).toFixed(1) + "L" : total >= 1000 ? "₹" + (total / 1000).toFixed(1) + "K" : "₹" + total.toFixed(0)}
      </text>
    </svg>
  </div>;
}


// ─── EMI CALCULATOR (full-screen overlay) ─────────────────────────
function EMICalculator({ onClose, T }) {
  const [principal, setPrincipal] = useState("");
  const [rate, setRate] = useState("");         // annual %
  const [months, setMonths] = useState("");
  const [procFee, setProcFee] = useState("");   // rupees
  const [showSchedule, setShowSchedule] = useState(false);

  // Parse inputs
  const p = parseFloat(principal) || 0;
  const r = parseFloat(rate) || 0;
  const n = parseInt(months) || 0;
  const pf = parseFloat(procFee) || 0;

  const result = useMemo(() => {
    if (p <= 0 || n <= 0) return null;
    const monthlyRate = r / 12 / 100;
    let emi = 0;
    if (monthlyRate === 0) {
      emi = p / n;
    } else {
      const pow = Math.pow(1 + monthlyRate, n);
      emi = (p * monthlyRate * pow) / (pow - 1);
    }
    const totalPayable = emi * n;
    const totalInterest = totalPayable - p;
    const trueCost = totalPayable + pf; // what you really pay including processing fee
    // Effective rate (APR-ish) factoring in processing fee — iterative solve
    let effectiveRate = r;
    if (pf > 0 && p > 0) {
      // Solve for rate where EMI * n = p + p*r*extra... simplified approximation:
      // treat processing fee as extra interest amortized over tenure
      const adjustedInterest = totalInterest + pf;
      // Simple approximation: average outstanding balance ≈ p/2, so annual rate:
      effectiveRate = (adjustedInterest / (p / 2)) * (12 / n) * 100;
    }

    // Build amortization schedule
    const schedule = [];
    let balance = p;
    for (let i = 1; i <= n; i++) {
      const interestPart = balance * monthlyRate;
      const principalPart = emi - interestPart;
      balance = Math.max(0, balance - principalPart);
      schedule.push({
        month: i,
        emi,
        principal: principalPart,
        interest: interestPart,
        balance,
      });
    }

    return { emi, totalPayable, totalInterest, trueCost, effectiveRate, schedule };
  }, [p, r, n, pf]);

  const inputStyle = {
    width: "100%",
    padding: "14px 16px",
    background: T.card,
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    color: T.text,
    fontSize: 15,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  };
  const labelStyle = { fontSize: 11, color: T.textMuted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 };

  const fmtINR = (n) => "₹" + (n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return <div style={{ position: "fixed", inset: 0, background: T.bg, zIndex: 150, display: "flex", flexDirection: "column" }}>
    {/* Header */}
    <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
      <button onClick={onClose} style={{ background: "transparent", border: "none", color: T.textSec, fontSize: 24, cursor: "pointer", padding: "0 4px" }}>‹</button>
      <div>
        <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: 3, textTransform: "uppercase", fontWeight: 500 }}>Vault</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>EMI Calculator</div>
      </div>
    </div>

    {/* Content */}
    <div style={{ flex: 1, overflowY: "auto", padding: "20px 20px 40px" }}>
      {/* Inputs */}
      <div style={{ marginBottom: 20 }}>
        <div style={labelStyle}>Loan amount (₹)</div>
        <input type="number" inputMode="decimal" value={principal} onChange={e => setPrincipal(e.target.value)} placeholder="e.g. 50000" style={inputStyle} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={labelStyle}>Interest rate (% per year)</div>
        <input type="number" inputMode="decimal" value={rate} onChange={e => setRate(e.target.value)} placeholder="e.g. 12 · use 0 for no-cost EMI" style={inputStyle} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={labelStyle}>Tenure (months)</div>
        <input type="number" inputMode="numeric" value={months} onChange={e => setMonths(e.target.value)} placeholder="e.g. 12" style={inputStyle} />
      </div>
      <div style={{ marginBottom: 24 }}>
        <div style={labelStyle}>Processing fee (₹, optional)</div>
        <input type="number" inputMode="decimal" value={procFee} onChange={e => setProcFee(e.target.value)} placeholder="e.g. 499" style={inputStyle} />
      </div>

      {/* Results */}
      {result ? <>
        {/* Headline EMI card */}
        <div style={{
          textAlign: "center",
          padding: "24px 20px",
          marginBottom: 16,
          borderRadius: 20,
          background: `linear-gradient(135deg, ${T.accent}15, ${T.accent}05)`,
          border: `1px solid ${T.accent}30`,
          position: "relative", overflow: "hidden",
        }}>
          <div style={{ position: "absolute", top: -50, right: -50, width: 150, height: 150, borderRadius: "50%", background: `radial-gradient(circle, ${T.accent}20, transparent)`, pointerEvents: "none" }} />
          <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: 3, textTransform: "uppercase", fontWeight: 500 }}>Monthly EMI</div>
          <div style={{ fontSize: 38, fontWeight: 800, background: T.accentGrad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginTop: 6, letterSpacing: -1 }}>
            {fmtINR(result.emi)}
          </div>
          <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>for {n} months</div>
        </div>

        {/* Breakdown card */}
        <div style={{ background: T.card, borderRadius: 14, padding: 16, border: `1px solid ${T.border}`, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontSize: 13, color: T.textSec }}>Principal</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{fmtINR(p)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontSize: 13, color: T.textSec }}>Total interest</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.red }}>{fmtINR(result.totalInterest)}</span>
          </div>
          {pf > 0 && <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontSize: 13, color: T.textSec }}>Processing fee</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.red }}>{fmtINR(pf)}</span>
          </div>}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
            <span style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>Total payable</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: T.accent }}>{fmtINR(result.trueCost)}</span>
          </div>
        </div>

        {/* No-cost reality check */}
        {r === 0 && pf > 0 && <div style={{ padding: "12px 16px", background: T.yellow + "15", border: `1px solid ${T.yellow}40`, borderRadius: 12, marginBottom: 16, fontSize: 12, color: T.yellow }}>
          ⚠ Not really no-cost. Processing fee of {fmtINR(pf)} makes your effective rate ~{result.effectiveRate.toFixed(1)}% per year.
        </div>}

        {/* Effective rate callout when pf > 0 and r > 0 */}
        {r > 0 && pf > 0 && <div style={{ padding: "10px 16px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, marginBottom: 16, fontSize: 12, color: T.textSec }}>
          Effective rate including processing fee: <span style={{ color: T.accent, fontWeight: 600 }}>~{result.effectiveRate.toFixed(1)}% per year</span>
        </div>}

        {/* Amortization schedule */}
        <button onClick={() => setShowSchedule(s => !s)} style={{
          width: "100%", padding: "12px 16px",
          background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
          color: T.textSec, fontSize: 13, cursor: "pointer",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: showSchedule ? 12 : 0,
        }}>
          <span>Month-by-month breakdown</span>
          <span style={{ fontSize: 10, color: T.textDim, transition: "transform 0.2s", transform: showSchedule ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
        </button>

        {showSchedule && <div style={{ background: T.card, borderRadius: 12, border: `1px solid ${T.border}`, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 1fr 1fr", gap: 8, padding: "10px 12px", borderBottom: `1px solid ${T.border}`, fontSize: 10, color: T.textMuted, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>
            <span>#</span>
            <span style={{ textAlign: "right" }}>Principal</span>
            <span style={{ textAlign: "right" }}>Interest</span>
            <span style={{ textAlign: "right" }}>Balance</span>
          </div>
          {result.schedule.map(row => <div key={row.month} style={{
            display: "grid", gridTemplateColumns: "40px 1fr 1fr 1fr", gap: 8,
            padding: "8px 12px",
            borderBottom: row.month === result.schedule.length ? "none" : `1px solid ${T.bg}`,
            fontSize: 12,
          }}>
            <span style={{ color: T.textDim }}>{row.month}</span>
            <span style={{ textAlign: "right", color: T.textSec }}>{fmtINR(row.principal)}</span>
            <span style={{ textAlign: "right", color: T.textDim }}>{fmtINR(row.interest)}</span>
            <span style={{ textAlign: "right", color: T.text }}>{fmtINR(row.balance)}</span>
          </div>)}
        </div>}
      </> : (
        <div style={{ padding: 40, textAlign: "center", color: T.textDim, fontSize: 13 }}>
          Enter loan amount and tenure to calculate
        </div>
      )}
    </div>
  </div>;
}

// ─── EMI TRACKER ───────────────────────────────────────────────────
// Module-level cache, 2-min TTL (cleared on Refresh from Settings)
let _emiCache = null;
function getEmiCache() {
  if (!_emiCache) return null;
  if (Date.now() - _emiCache.fetchedAt > 2 * 60 * 1000) { _emiCache = null; return null; }
  return _emiCache.data;
}
function setEmiCache(data) { _emiCache = { data, fetchedAt: Date.now() }; }
function clearEmiCache() { _emiCache = null; }

// Transform raw {configs, logs} into per-EMI summary cards
function buildEmiData(raw) {
  if (!raw || !raw.configs) return { active: [], closed: [] };
  const configs = raw.configs || [];
  const logs = raw.logs || [];

  // Group logs by item+card key
  const logsByKey = new Map();
  logs.forEach(l => {
    const item = String(l.Item || l.item || "").trim();
    const card = String(l.Card || l.card || "").trim();
    if (!item || !card) return;
    const key = `${item}::${card}`;
    if (!logsByKey.has(key)) logsByKey.set(key, []);
    logsByKey.get(key).push(l);
  });

  const result = configs.map(cfg => {
    const item = String(cfg.Item || cfg.item || "").trim();
    const card = String(cfg.Card || cfg.card || "").trim();
    const total = parseFloat(cfg["Total Amount"] || cfg.total || 0) || 0;
    const months = parseInt(cfg["Total Months"] || cfg.months || 0) || 0;
    const keyword = String(cfg["Keyword (bot note prefix)"] || cfg.keyword || "").trim();
    const key = `${item}::${card}`;
    const itemLogs = logsByKey.get(key) || [];

    // Sum amount paid (logs store as negative — we want absolute sum)
    const amountPaid = itemLogs.reduce((s, l) => {
      const amt = parseFloat(l["Amount Paid"] || l.amountPaid || l.amount || 0);
      return s + Math.abs(amt) || s;
    }, 0);
    const remaining = Math.max(0, total - amountPaid);
    // EMI count: prefer max EMI # column, fallback to count of rows
    let paidCount = 0;
    itemLogs.forEach(l => {
      const n = parseInt(l["EMI #"] || l.emiNum || 0);
      if (!isNaN(n) && n > paidCount) paidCount = n;
    });
    if (paidCount === 0) paidCount = itemLogs.length;
    const emisLeft = Math.max(0, months - paidCount);
    const monthlyEmi = months > 0 ? total / months : 0;
    const isClosed = remaining <= 0.01 || (months > 0 && paidCount >= months);

    // Sort logs newest-first for the drill-down
    const sortedLogs = [...itemLogs].sort((a, b) => {
      const an = parseInt(a["EMI #"] || a.emiNum || 0);
      const bn = parseInt(b["EMI #"] || b.emiNum || 0);
      return bn - an;
    });

    return {
      item, card, total, months, keyword,
      amountPaid, remaining, paidCount, emisLeft, monthlyEmi, isClosed,
      logs: sortedLogs,
    };
  });

  return {
    active: result.filter(e => !e.isClosed).sort((a, b) => b.emisLeft - a.emisLeft),
    closed: result.filter(e => e.isClosed),
  };
}

function EmiScreen({ T, theme }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null); // EMI key currently expanded
  const [showClosed, setShowClosed] = useState(false);

  useEffect(() => {
    const cached = getEmiCache();
    if (cached) { setData(cached); setLoading(false); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    api.get("/api/emi")
      .then(d => {
        if (cancelled) return;
        setEmiCache(d);
        setData(d); setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(e.message || "Failed"); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingState T={T} />;
  if (error) return <div style={{ padding: 40, textAlign: "center", color: T.red, fontSize: 13 }}>Failed: {error}</div>;
  const { active, closed } = buildEmiData(data);

  if (active.length === 0 && closed.length === 0) {
    return <div style={{ padding: "40px 24px", textAlign: "center", color: T.textDim, fontSize: 13 }}>
      <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>%</div>
      <div style={{ fontSize: 15, color: T.textSec, marginBottom: 8 }}>No EMIs yet</div>
      <div style={{ lineHeight: 1.5 }}>
        Add a row to your <span style={{ color: T.accent, fontWeight: 600 }}>EMI Config</span> sheet with item, card, total amount, months, and keyword.
        <br /><br />
        The bot will auto-log payments to <span style={{ color: T.accent, fontWeight: 600 }}>EMI Log</span> when it sees the keyword in a transaction note.
      </div>
    </div>;
  }

  return <div style={{ padding: "0 16px 100px", overflowY: "auto", height: "100%" }}>
    {/* Active EMIs */}
    {active.length > 0 && <>
      <FadeIn delay={0}>
        <div style={{ marginTop: 16, marginBottom: 10, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: T.accent, fontWeight: 600 }}>
          Active EMIs ({active.length})
        </div>
      </FadeIn>
      {active.map((emi, i) => (
        <EmiCard
          key={`a-${emi.item}-${emi.card}`}
          emi={emi}
          isExpanded={expanded === `${emi.item}::${emi.card}`}
          onToggle={() => setExpanded(expanded === `${emi.item}::${emi.card}` ? null : `${emi.item}::${emi.card}`)}
          T={T} theme={theme} delay={i * 50}
        />
      ))}
    </>}

    {/* Closed EMIs (collapsible) */}
    {closed.length > 0 && <>
      <FadeIn delay={200}>
        <button onClick={() => setShowClosed(s => !s)} style={{
          width: "100%", marginTop: 24, marginBottom: 10,
          padding: "10px 14px",
          background: "transparent", border: `1px solid ${T.border}`, borderRadius: 12,
          color: T.textMuted, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase",
          cursor: "pointer",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontWeight: 600,
        }}>
          <span>Closed EMIs ({closed.length})</span>
          <span style={{ fontSize: 10, transition: "transform 0.2s", transform: showClosed ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
        </button>
      </FadeIn>
      {showClosed && closed.map((emi, i) => (
        <EmiCard
          key={`c-${emi.item}-${emi.card}`}
          emi={emi}
          isExpanded={expanded === `${emi.item}::${emi.card}`}
          onToggle={() => setExpanded(expanded === `${emi.item}::${emi.card}` ? null : `${emi.item}::${emi.card}`)}
          T={T} theme={theme} delay={i * 50}
        />
      ))}
    </>}
  </div>;
}

function EmiCard({ emi, isExpanded, onToggle, T, theme, delay }) {
  const style = bankStyle(emi.card, theme);
  const accentGradient = style.accent2
    ? `linear-gradient(90deg, ${style.accent}, ${style.accent2}, transparent)`
    : `linear-gradient(90deg, ${style.accent}, transparent)`;
  const progressPct = emi.months > 0 ? (emi.paidCount / emi.months) * 100 : 0;

  return <FadeIn delay={delay}>
    <div onClick={onToggle} style={{
      position: "relative",
      borderRadius: 18,
      padding: "18px 20px 16px",
      marginBottom: 12,
      overflow: "hidden",
      cursor: "pointer",
      border: `1px solid ${style.border}`,
      background: `linear-gradient(135deg, ${style.bgFrom} 0%, ${style.bgTo} 100%)`,
      boxShadow: theme === "light" ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
    }}>
      {/* Accent bar */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accentGradient, opacity: 0.9 }} />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: style.textLabel, fontWeight: 600, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 8, display: "inline-block", transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
            {style.label || emi.card}
            {emi.isClosed && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: T.green + "33", color: T.green, fontWeight: 600, letterSpacing: 1 }}>CLOSED</span>}
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: style.textPrimary, letterSpacing: "-0.3px", lineHeight: 1.15 }}>
            {emi.item}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: style.textLabel, fontWeight: 600 }}>
            {emi.isClosed ? "Total Paid" : "Remaining"}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: emi.isClosed ? T.green : T.accent, marginTop: 2, letterSpacing: "-0.5px" }}>
            {fmtShort(emi.isClosed ? emi.amountPaid : emi.remaining)}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ marginTop: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 11, color: style.textFootMuted }}>
          <span>{emi.paidCount} of {emi.months} EMIs paid</span>
          <span style={{ fontWeight: 600, color: style.textFoot }}>{progressPct.toFixed(0)}%</span>
        </div>
        <div style={{ width: "100%", height: 6, background: theme === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.1)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${progressPct}%`, height: "100%", background: emi.isClosed ? T.green : style.accent, borderRadius: 3, transition: "width 0.5s ease" }} />
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, paddingTop: 12, borderTop: `1px solid ${style.divider}`, fontSize: 12 }}>
        <div style={{ color: style.textFoot, fontWeight: 500 }}>
          Monthly: <span style={{ color: style.textPrimary, fontWeight: 600 }}>{fmtFull(emi.monthlyEmi)}</span>
        </div>
        {!emi.isClosed && <div style={{ color: style.textFootMuted }}>
          {emi.emisLeft} {emi.emisLeft === 1 ? "EMI" : "EMIs"} left
        </div>}
        {emi.isClosed && <div style={{ color: T.green, fontWeight: 600, fontSize: 11 }}>Fully paid ✓</div>}
      </div>

      {/* Expanded payment history */}
      {isExpanded && (
        <div onClick={e => e.stopPropagation()} style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${style.divider}` }}>
          <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: style.textLabel, marginBottom: 8, fontWeight: 600 }}>
            Payment History ({emi.logs.length})
          </div>
          {emi.logs.length === 0 ? (
            <div style={{ padding: 12, textAlign: "center", color: style.textFootMuted, fontSize: 12 }}>
              No payments logged yet. The bot will populate this when it sees "{emi.keyword}" in a transaction note.
            </div>
          ) : (
            <div style={{ background: theme === "light" ? "rgba(0,0,0,0.03)" : "rgba(0,0,0,0.25)", borderRadius: 8, padding: "4px 10px" }}>
              {emi.logs.map((log, i) => {
                const num = log["EMI #"] || log.emiNum || (i + 1);
                const month = log["Month"] || log.month || "—";
                const amt = Math.abs(parseFloat(log["Amount Paid"] || log.amountPaid || log.amount || 0));
                return <div key={i} style={{
                  display: "flex", justifyContent: "space-between",
                  padding: "8px 4px",
                  borderBottom: i === emi.logs.length - 1 ? "none" : `1px solid ${style.divider}`,
                  fontSize: 12,
                }}>
                  <div style={{ color: style.textPrimary }}>
                    <span style={{ color: style.textLabel, fontWeight: 600, marginRight: 6 }}>#{num}</span>
                    {month}
                  </div>
                  <div style={{ color: style.textPrimary, fontWeight: 600 }}>{fmtFull(amt)}</div>
                </div>;
              })}
            </div>
          )}
        </div>
      )}
    </div>
  </FadeIn>;
}

// ─── SETTINGS SCREEN ───────────────────────────────────────────────
function SettingsScreen({ onRefresh, onToast, summaryData, T, theme, setTheme }) {
  const [showDel, setShowDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEMI, setShowEMI] = useState(false);
  const handleDelete = async () => { setDeleting(true); try { const r = await api.del("/api/transactions/last"); if (r.success) { onToast(`Deleted: ${r.deleted?.note || "transaction"} (${fmtFull(parseFloat(r.deleted?.amount || 0))})`, "success"); setShowDel(false); } } catch { onToast("Failed to delete", "error"); } setDeleting(false); };
  const cards = summaryData?.creditCards || [];

  const Btn = ({ icon, label, desc, onClick }) => <button onClick={onClick} style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: 16, background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, marginBottom: 8, cursor: "pointer", textAlign: "left" }}>
    <span style={{ fontSize: 20, width: 28, textAlign: "center", color: T.accent }}>{icon}</span>
    <div style={{ flex: 1 }}><div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{label}</div><div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{desc}</div></div>
    <span style={{ color: T.textDim, fontSize: 18 }}>›</span>
  </button>;

  return <div style={{ padding: "0 20px 100px", overflowY: "auto", height: "100%" }}>
    <div style={{ paddingTop: 12 }}>
      <FadeIn delay={0}><Btn icon="↻" label="Refresh Data" desc="Pull latest from Google Sheets" onClick={onRefresh} /></FadeIn>
      <FadeIn delay={50}><Btn icon="✕" label="Delete Last Transaction" desc="Remove with confirmation" onClick={() => setShowDel(true)} /></FadeIn>
      <FadeIn delay={100}><Btn icon="%" label="EMI Calculator" desc="Calculate monthly EMI, interest, and total cost" onClick={() => setShowEMI(true)} /></FadeIn>
      <FadeIn delay={150}>
        <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: 16, background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, marginBottom: 8, cursor: "pointer", textAlign: "left" }}>
          <span style={{ fontSize: 20, width: 28, textAlign: "center" }}>{theme === "dark" ? "☀" : "🌙"}</span>
          <div style={{ flex: 1 }}><div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{theme === "dark" ? "Light Theme" : "Dark Theme"}</div><div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>Switch to {theme === "dark" ? "light" : "dark"} mode</div></div>
          <span style={{ color: T.textDim, fontSize: 18 }}>›</span>
        </button>
      </FadeIn>
      <FadeIn delay={200}><div style={{ textAlign: "center", marginTop: 32, padding: 20, color: T.textDim, fontSize: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.accent, marginBottom: 4, letterSpacing: 2 }}>VAULT</div>
        <div>Personal Finance Tracker</div><div style={{ marginTop: 4 }}>v1.1.0</div>
        {txQueue.pending.length > 0 && <div style={{ marginTop: 8, color: T.yellow, fontSize: 11 }}>⏳ {txQueue.pending.length} syncing...</div>}
      </div></FadeIn>
    </div>
    {showDel && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowDel(false)}>
      <div style={{ background: T.modalBg, borderRadius: 20, padding: 24, width: "85%", maxWidth: 360, border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 8 }}>Delete Last Transaction?</div>
        <div style={{ fontSize: 14, color: T.textMuted, marginBottom: 24 }}>This will permanently remove the most recent transaction.</div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={() => setShowDel(false)} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: `1px solid ${T.border}`, background: "transparent", color: T.textMuted, fontSize: 14, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleDelete} disabled={deleting} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "none", background: "#7f1d1d", color: "#f87171", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>{deleting ? "Deleting..." : "Delete"}</button>
        </div>
      </div>
    </div>}
    {showEMI && <EMICalculator onClose={() => setShowEMI(false)} T={T} />}
  </div>;
}

// ─── SHARED COMPONENTS ─────────────────────────────────────────────
function LoadingState({ T }) { return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "50%", gap: 16 }}><div style={{ width: 40, height: 40, border: `3px solid ${T.border}`, borderTopColor: T.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /><span style={{ color: T.textMuted, fontSize: 13 }}>Loading...</span></div>; }
function EmptyState({ message, onAction, actionLabel, T }) { return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "50%", gap: 16 }}><span style={{ color: T.textMuted, fontSize: 14 }}>{message}</span>{onAction && <button onClick={onAction} style={{ padding: "10px 24px", borderRadius: 10, border: `1px solid ${T.accent}`, background: "transparent", color: T.accent, fontSize: 14, cursor: "pointer" }}>{actionLabel}</button>}</div>; }

// ─── MAIN APP ──────────────────────────────────────────────────────
export default function App() {
  const [tab, setTab] = useState("add");
  const [accounts, setAccounts] = useState([]);
  const [labels, setLabels] = useState([]);
  const [categories, setCategories] = useState({});
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dataLoading, setDataLoading] = useState(true);
  const [recentAccounts, setRecentAccounts] = useState([]);
  const [recentLabels, setRecentLabels] = useState([]);
  const [hideAmounts, setHideAmounts] = useState(() => { try { return window.localStorage?.getItem("vault-hide") === "1"; } catch { return false; } });
  const [locked, setLocked] = useState(true);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(false);
  const APP_PIN = "6030";
  const [theme, setTheme] = useState(() => {
    try { return window.localStorage?.getItem("vault-theme") || "dark"; } catch { return "dark"; }
  });
  const T = themes[theme] || themes.dark;
  if (typeof window !== "undefined") window.__vaultHide = hideAmounts;

  useEffect(() => { try { window.localStorage?.setItem("vault-theme", theme); } catch {} }, [theme]);
  useEffect(() => { try { window.localStorage?.setItem("vault-hide", hideAmounts ? "1" : "0"); } catch {} window.__vaultHide = hideAmounts; }, [hideAmounts]);

  // PIN entry handler
  const handlePinDigit = (d) => {
    if (pin.length >= 4) return;
    const newPin = pin + d;
    setPin(newPin);
    setPinError(false);
    if (newPin.length === 4) {
      if (newPin === APP_PIN) {
        setTimeout(() => setLocked(false), 200);
      } else {
        setPinError(true);
        setTimeout(() => { setPin(""); setPinError(false); }, 600);
      }
    }
  };
  const handlePinDelete = () => { setPin(p => p.slice(0, -1)); setPinError(false); };

  const FL = [{ name: "Mummy" }, { name: "Friends & Family" }, { name: "PhonePe" }, { name: "Amazon/AmazonPay" }, { name: "Cred" }, { name: "Gpay" }, { name: "Paytm" }];
  const FC = { "Food & Dining": ["Groceries", "Vegetables & Fruits", "Dining Out", "Food Delivery", "Snacks & Beverages"], "Vehicle & Transport": ["Petrol", "Vehicle Maintenance & Repair", "Toll & Parking", "Auto / Cab / Rickshaw"], "Shopping": ["Clothing & Footwear", "Electronics & Gadgets", "Home & Kitchen", "Books & Stationery"], "Health & Medical": ["Medicines", "Doctor Consultation", "Hospital & Tests"], "Home & Living": ["Rent", "Electricity", "Home Maintenance", "Household Supplies"], "Bills & Utilities": ["Mobile Recharge", "Internet & Broadband", "OTT Subscriptions"], "Child": ["Toys & Games", "Doctor & Vaccines", "Snacks & Treats"], "Finance & Banking": ["Credit Card Payment", "EMI", "Bank Charges & Fees"], "Entertainment": ["Movies & Events", "Games & Hobbies"], "Travel": ["Hotels & Stay", "Local Travel"], "Work & Content Creation": ["Equipment & Tools", "Office Supplies"], "Education & Learning": ["Courses & Books"], "Social & Giving": ["Donations & Charity", "Wedding & Functions"], "Personal Care": ["Grooming & Haircut", "Gym & Fitness"], "Income & Transfers": ["Salary", "Cashback & Rewards", "Refund Received", "Account Transfer"], "Miscellaneous": ["Uncategorised"] };

  const showToast = useCallback((m, t) => setToast({ msg: m, type: t }), []);
  const loadInit = useCallback(async () => { setDataLoading(true); try { const [a, l, c] = await Promise.all([api.get("/api/accounts"), api.get("/api/labels"), api.get("/api/categories")]); setAccounts(a.accounts || []); setLabels(l.labels?.length ? l.labels : FL); setCategories(Object.keys(c.categories || {}).length ? c.categories : FC); } catch { setLabels(FL); setCategories(FC); } setDataLoading(false); }, []);
  // Fetch recent accounts AND recent labels from combined report (current cycle CC + this month savings)
  const loadRecent = useCallback(async () => {
    try {
      const r = await api.get("/api/report/combined");
      const txs = [...(r.own||[]),...(r.mummy||[]),...(r.ff||[]),...(r.refunds||[])];
      const parseTS = (s) => {
        if (!s) return 0;
        const m = String(s).replace("T"," ").match(/(\d{4})-(\d{2})-(\d{2})\s*(\d{2})?:?(\d{2})?:?(\d{2})?/);
        return m ? new Date(+m[1],+m[2]-1,+m[3],+(m[4]||0),+(m[5]||0),+(m[6]||0)).getTime() : 0;
      };
      txs.sort((a,b) => parseTS(b.timestamp) - parseTS(a.timestamp));
      // Recent accounts — unique names in most-recent order
      const seenAcc = [];
      txs.forEach(t => { if (t.account && !seenAcc.includes(t.account)) seenAcc.push(t.account); });
      setRecentAccounts(seenAcc.slice(0, 5));
      // Recent labels — count usage frequency across all tx labels
      const labelCount = new Map();
      txs.forEach(t => {
        String(t.labels || "").split(",").map(s => s.trim()).filter(Boolean).forEach(l => {
          labelCount.set(l, (labelCount.get(l) || 0) + 1);
        });
      });
      const sortedLabels = [...labelCount.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name);
      setRecentLabels(sortedLabels);
    } catch {}
  }, []);
  const loadSummary = useCallback(async () => { setLoading(true); try { const data = await api.get("/api/summary"); setSummaryData(data); showToast("Summary updated", "success"); } catch (e) { console.error("Summary fetch failed:", e); showToast("Failed — server may be starting up. Wait 30s and retry.", "error"); } setLoading(false); }, [showToast]);

  useEffect(() => { loadInit(); loadRecent(); }, [loadInit, loadRecent]);
  useEffect(() => { if (tab === "summary" && !summaryData) loadSummary(); }, [tab, summaryData, loadSummary]);

  const handleRefresh = () => { txDetailCache.clear(); clearChartCache(); clearEmiCache(); setSummaryData(null); setRefreshKey(k => k + 1); setTab("summary"); loadSummary(); loadInit(); };

  // PIN Lock Screen
  if (locked) {
    return <div style={{ width: "100%", maxWidth: 420, margin: "0 auto", height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: T.bg, fontFamily: "'DM Sans', 'Segoe UI', sans-serif", color: T.text }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`@keyframes shake { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-8px); } 75% { transform: translateX(8px); } }`}</style>
      <div style={{ fontSize: 14, color: T.textMuted, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>Vault</div>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: T.accentGrad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, color: "#0a0f1e", marginBottom: 32 }}>V</div>
      <div style={{ fontSize: 16, color: T.textSec, marginBottom: 24 }}>Enter PIN</div>
      <div style={{ display: "flex", gap: 16, marginBottom: 32, animation: pinError ? "shake 0.3s ease" : "none" }}>
        {[0,1,2,3].map(i => <div key={i} style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${pinError ? T.red : T.accent}`, background: i < pin.length ? (pinError ? T.red : T.accent) : "transparent", transition: "all 0.15s" }} />)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, width: 260 }}>
        {[1,2,3,4,5,6,7,8,9,"",0,"⌫"].map((d, i) => d === "" ? <div key={i} /> : <button key={i} onClick={() => d === "⌫" ? handlePinDelete() : handlePinDigit(String(d))} style={{ width: 72, height: 56, borderRadius: 14, border: `1px solid ${T.border}`, background: T.card, color: T.text, fontSize: d === "⌫" ? 20 : 24, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center" }}>{d}</button>)}
      </div>
    </div>;
  }

  const tabs = [
    { id: "add", label: "Add", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg> },
    { id: "summary", label: "Summary", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg> },
    { id: "reports", label: "Reports", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12V7H5a2 2 0 010-4h14v4"/><path d="M3 5v14a2 2 0 002 2h16v-5"/><path d="M18 12a2 2 0 100 4h4v-4h-4z"/></svg> },
    { id: "charts", label: "Charts", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
    { id: "emi", label: "EMI", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg> },
    { id: "settings", label: "Settings", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg> },
  ];
  const titles = { add: "Add Transaction", summary: "Summary", reports: "Reports", charts: "Charts", emi: "EMI", settings: "Settings" };

  return <div style={{ width: "100%", maxWidth: 420, margin: "0 auto", height: "100vh", display: "flex", flexDirection: "column", background: T.bg, fontFamily: "'DM Sans', 'Segoe UI', sans-serif", color: T.text, overflow: "hidden", position: "relative", transition: "background 0.3s, color 0.3s" }}>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
    <style>{`
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      @keyframes slideDown { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
      ::-webkit-scrollbar { width: 0; height: 0; background: transparent; }
      ::-webkit-scrollbar-thumb { background: transparent; }
      * { scrollbar-width: none; -ms-overflow-style: none; }
      input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
      input[type=number] { -moz-appearance: textfield; }
      button { font-family: inherit; }
    `}</style>
    {toast && <Toast message={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    <div style={{ padding: "14px 20px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${T.headerBorder}`, flexShrink: 0 }}>
      <div><div style={{ fontSize: 10, color: T.textMuted, letterSpacing: 3, textTransform: "uppercase", fontWeight: 500 }}>Vault</div><div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{titles[tab]}</div></div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={() => setHideAmounts(h => !h)} style={{ width: 36, height: 36, borderRadius: 10, background: "transparent", border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.textMuted, padding: 0 }}>
          {hideAmounts ?
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
        </button>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: T.accentGrad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#0a0f1e" }}>D</div>
      </div>
    </div>
    <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
      {tab === "add" && <AddScreen accounts={accounts} labels={labels.length ? labels : FL} categories={Object.keys(categories).length ? categories : FC} onToast={showToast} T={T} recentAccounts={recentAccounts} recentLabels={recentLabels} dataLoading={dataLoading} onTxAdded={() => { setRefreshKey(k => k + 1); setSummaryData(null); loadRecent(); }} />}
      {tab === "summary" && <SummaryScreen summaryData={summaryData} loading={loading} onRefresh={loadSummary} T={T} theme={theme} />}
      {tab === "reports" && <ReportsScreen key={refreshKey} T={T} />}
      {tab === "charts" && <ChartsScreen key={refreshKey} T={T} theme={theme} />}
      {tab === "emi" && <EmiScreen key={refreshKey} T={T} theme={theme} />}
      {tab === "settings" && <SettingsScreen onRefresh={handleRefresh} onToast={showToast} summaryData={summaryData} T={T} theme={theme} setTheme={setTheme} />}
    </div>
    <div style={{ display: "flex", borderTop: `1px solid ${T.headerBorder}`, background: T.navBg, paddingBottom: "env(safe-area-inset-bottom, 8px)", flexShrink: 0 }}>
      {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "10px 0 6px", background: "transparent", border: "none", cursor: "pointer", color: tab === t.id ? T.accent : T.textDim, transition: "color 0.2s" }}>
        {t.icon}<span style={{ fontSize: 10, fontWeight: tab === t.id ? 700 : 400, letterSpacing: 0.5 }}>{t.label}</span>
        {tab === t.id && <div style={{ width: 16, height: 2, borderRadius: 1, background: T.accent, marginTop: 1 }} />}
      </button>)}
    </div>
  </div>;
}
